/* Built by datalayer/build.js -- DO NOT EDIT DIST FILES, edit src/ and rebuild. */
;(function (window, document) {
'use strict';
var DL = {};
/* ============================================================================
 * core/safe.js -- null-safe access + handler isolation (fixes C1)
 *
 * Shopify silently swallows a throwing pixel handler: the event is simply
 * lost with no error anywhere. Every handler MUST be wrapped, and every
 * nested read MUST go through get(). No exceptions to this rule.
 * ========================================================================== */

DL.safe = (function () {
  /**
   * Null-safe deep read.
   *   get(evt, 'data.checkout.shippingLine.price.amount')  -> value | undefined
   * Never throws, whatever is null/undefined along the path.
   */
  function get(obj, path, fallback) {
    if (obj == null) return fallback;
    var parts = path.split('.');
    var cur = obj;
    for (var i = 0; i < parts.length; i++) {
      if (cur == null) return fallback;
      cur = cur[parts[i]];
    }
    return cur == null ? fallback : cur;
  }

  /**
   * Wrap a handler so an internal bug can never kill the event pipeline
   * (or, on the theme side, the merchant's own code). Errors are surfaced
   * in debug mode instead of vanishing.
   */
  function wrap(name, fn) {
    return function () {
      try {
        return fn.apply(null, arguments);
      } catch (err) {
        DL.debug.error('handler "' + name + '" threw -- event dropped', err);
        return undefined;
      }
    };
  }

  /** Async variant: resolves undefined on failure, never rejects. */
  function wrapAsync(name, fn) {
    return function () {
      var args = arguments;
      return new Promise(function (resolve) {
        try {
          Promise.resolve(fn.apply(null, args)).then(resolve, function (err) {
            DL.debug.error('async handler "' + name + '" rejected -- event dropped', err);
            resolve(undefined);
          });
        } catch (err) {
          DL.debug.error('async handler "' + name + '" threw -- event dropped', err);
          resolve(undefined);
        }
      });
    };
  }

  return { get: get, wrap: wrap, wrapAsync: wrapAsync };
})();

/* ============================================================================
 * core/debug.js -- trace mode
 *
 * Requirement: "sob event jeno trace kora jay" -- both directions:
 *   - every event that FIRES is logged with its full payload
 *   - every event that is DROPPED is logged with the reason
 * Silent loss is the reference script's habit; it is banned here.
 * ========================================================================== */

DL.debug = (function () {
  var PREFIX = '[datalayer]';

  function enabled() {
    return !!(DL.config && DL.config.debug);
  }

  function log() {
    if (!enabled() || typeof console === 'undefined') return;
    var args = [PREFIX].concat([].slice.call(arguments));
    console.log.apply(console, args);
  }

  function warn() {
    if (typeof console === 'undefined') return;
    // warnings show even without debug -- they indicate misconfiguration
    var args = [PREFIX].concat([].slice.call(arguments));
    console.warn.apply(console, args);
  }

  function error() {
    if (typeof console === 'undefined') return;
    var args = [PREFIX].concat([].slice.call(arguments));
    console.error.apply(console, args);
  }

  /** An event went out. */
  function trace(evt) {
    log('EVENT ->', evt.event, evt);
  }

  /**
   * An event did NOT go out. reason in
   *   'validation-failed' | 'consent-blocked' | 'dedup-blocked' |
   *   'purchase-guard' | 'empty-items' | 'handler-error'
   */
  function drop(reason, name, detail) {
    log('DROPPED x', name, '(' + reason + ')', detail || '');
  }

  return { enabled: enabled, log: log, warn: warn, error: error, trace: trace, drop: drop };
})();

/* ============================================================================
 * core/config.js -- the ONLY place that changes per client
 *
 * Everything a new store needs is set here. No GTM id, currency, selector or
 * platform name may be hardcoded anywhere else in the codebase.
 * ========================================================================== */

DL.defaults = {
  gtm: {
    id: '',                    // REQUIRED -- e.g. 'GTM-XXXXXXX'
    // Stape custom loader friendly: point this at your own domain to resist
    // adblockers. Default is Google's origin.
    loaderUrl: 'https://www.googletagmanager.com/gtm.js',
    dataLayerName: 'dataLayer',
    // Load GTM inside the checkout pixel sandbox too (its own dataLayer --
    // the pixel and theme dataLayers are SEPARATE by Shopify design).
    loadInPixel: true
  },

  debug: false,                // trace mode: log every fired AND dropped event

  // Who owns the pre-checkout funnel events (theme-independence knob):
  //   'pixel' (default) -- the custom pixel emits the FULL funnel via Shopify
  //     STANDARD customer events (page_viewed, product_viewed, collection_viewed,
  //     search_submitted, product_added_to_cart, cart_viewed, checkout->purchase).
  //     These fire regardless of theme -> survives theme change/update. The theme
  //     snippet then only adds DOM-only enrichment (select_item, promotions, leads).
  //   'theme' -- the theme emits the funnel (richer top-frame GTM/Tag Assistant,
  //     but breaks if the theme changes). Pixel stays checkout-only.
  eventOwnership: 'pixel',

  // Report whatever the shopper does. When false (default) NOTHING is
  // suppressed: repeat a step, reload a page, go through checkout again -- each
  // action reports. Set true to restore the defensive guards (a 2.5s identical-
  // event window plus a once-per-order purchase marker), which is worth doing
  // only on a store whose theme is known to emit an event from two places.
  suppressDuplicates: false,

  // WHO emits remove_from_cart. Shopify's product_removed_from_cart is NOT
  // dependable across themes: it never fires for full-page carts (Remove link /
  // "Update cart" form) and is missed by themes that drive the cart through
  // their own API. The theme, however, sees BOTH paths -- the /cart/*.js AJAX
  // calls and the full-page ones -- so it is the universal owner. One owner
  // only: the pixel skips Shopify's removal event while this is 'theme',
  // otherwise the two worlds (sandbox + top frame) would each emit one and the
  // cross-world dedup cannot see the duplicate.
  //   'pixel' (default) -- the theme detects the removal and hands it to the
  //     pixel through Shopify.analytics.publish, so remove_from_cart appears in
  //     the SANDBOX dataLayer with event_source 'custom_pixel', exactly like
  //     every other funnel event. Falls back to a theme-side emit when the
  //     storefront has no publish API.
  //   'theme'           -- always emit from the theme (top-frame dataLayer).
  removalOwner: 'pixel',

  // Custom event name used for that theme -> pixel handover.
  removalBridgeEvent: 'dl_remove_from_cart',

  /* ------------------------------------------------------------------ items */
  // Must match the Merchant Center feed. Explicit config -- never guessed.
  itemIdSource: 'sku',         // 'sku' | 'variant_id' | 'product_id' | 'shopify_feed'
  // 'shopify_feed' builds the id Google's own Shopify app puts in the Merchant
  // Center feed: shopify_<COUNTRY>_<productId>_<variantId>. Use it when Google
  // Ads dynamic remarketing / Shopping must match the feed -- an id that does
  // not match the feed silently kills product matching. Set feedCountry to the
  // feed's target country (the middle segment of the Merchant Center offer id).
  feedCountry: 'US',
  // When sku is missing/null on an item, fall back to variant_id so item_id
  // is never empty.
  itemIdFallback: 'variant_id',

  /* ------------------------------------------------------------------ value */
  // ONE definition of purchase value, used identically everywhere, documented
  // to the client. #1 cause of GA4-vs-Shopify report mismatch.
  purchaseValueIncludes: { tax: true, shipping: true },

  /* ------------------------------------------------------------ server side */
  serverSide: {
    enabled: true              // sGTM (e.g. Stape) in use
  },

  /* -------------------------------------------------------------- platforms */
  // Browser-side platform parameter blocks (fb_browser_side etc).
  // With serverSide.enabled these default OFF -- the sGTM templates build the
  // per-platform payloads; duplicating them in the browser means three more
  // places to break. Force with browserPlatformBlocks: true.
  platforms: ['facebook', 'pinterest', 'snapchat', 'tiktok', 'linkedin', 'google_ads'],
  browserPlatformBlocks: null, // null = auto (!serverSide.enabled) | true | false

  /* ----------------------------------------------------------- destinations */
  // The per-platform matrix: WHO gets the event and from WHICH side.
  //   side 'client' -- browser tag/pixel
  //   side 'server' -- sGTM tag / Conversions API / our relay
  //   side 'both'   -- only where the vendor de-duplicates on event_id (Meta,
  //     TikTok, Pinterest, Snapchat, LinkedIn). GA4 and Google Ads refuse it:
  //     two copies there = double-counted conversions.
  // Enforced in core/destinations.js -- never per tag, so a merchant cannot
  // accidentally configure a duplicate.
  destinations: {
    ga4:        { enabled: false, side: 'client' },
    google_ads: { enabled: false, side: 'client', conversionId: '', purchaseLabel: '' },
    meta:       { enabled: false, side: 'client', pixelId: '' },
    tiktok:     { enabled: false, side: 'client', pixelId: '' },
    pinterest:  { enabled: false, side: 'client', tagId: '' },
    snapchat:   { enabled: false, side: 'client', pixelId: '' },
    linkedin:   { enabled: false, side: 'client', partnerId: '' }
  },

  /* ---------------------------------------------------------------- consent */
  consent: {
    // 'shopify' -- CMP syncs to Customer Privacy API; we read it and WE emit
    //             Consent Mode v2 default/update.
    // 'gtm'     -- client's GTM has its own consent template; we emit NO CMv2
    //             signals, only a consent_state push + the theme queue gate.
    // 'auto'    -- detect: Customer Privacy API present -> 'shopify', else 'gtm'.
    mode: 'auto',
    // 'cmp' (DEFAULT) -- DEFER: the client's CMP GTM template owns Consent Mode
    //   default/update + region logic (Google's one-owner rule; CMP-agnostic --
    //   swap the CMP template, our tags never change). We emit NO CMv2, only a
    //   consent_state push + the theme queue gate.
    // 'datalayer' -- opt-in ONLY for a store with no CMP at all: WE emit CMv2.
    owner: 'cmp',              // 'cmp' | 'datalayer'
    // Default-deny. Regions are the CMP's (storefront) / Shopify's (sandbox)
    // business, never ours -- we write no geo branches.
    defaultState: {
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      analytics_storage: 'denied'
    },
    waitForUpdateMs: 500,
    // CHECKOUT SANDBOX: the client's CMP JS cannot run inside Shopify's Web
    // Pixel sandbox, so the ONLY consent source there is Shopify's Customer
    // Privacy API. The pixel therefore ALWAYS maps it to Consent Mode signals,
    // regardless of the storefront `owner` above. Turn off only if the client
    // handles checkout consent entirely server-side.
    pixelEmitsConsent: true,
    // Consent Mode ADVANCED posture (applied by the pixel sandbox always, and
    // on the storefront only when owner==='datalayer';
    // when a CMP template owns consent, the template sets these itself).
    // Advanced = tags load pre-consent and send cookieless pings -> unlocks
    // GA4 behavioral modeling + full Ads conversion modeling.
    urlPassthrough: true,      // preserve gclid/analytics across pages when denied
    adsDataRedaction: true     // redact ad identifiers while ad_storage denied
  },

  /* ------------------------------------------------------------- event kill */
  // Any event name here is never emitted (Elevar-style per-event toggle).
  disabledEvents: [],

  /* -------------------------------------------------------------- selectors */
  // Theme DOM differs per theme; these are config, never hardcoded.
  // preset: 'dawn' | 'generic' -- override any key per store.
  selectorPreset: 'dawn',
  selectors: {},               // per-store overrides merged over the preset

  /* ------------------------------------------------------------------ leads */
  leads: {
    newsletterSelector: 'form[action*="contact#newsletter"], form.newsletter-form, form[action*="customer_posted"]',
    contactSelector: 'form[action*="/contact"]:not([action*="newsletter"])',
    trackTel: true, trackMailto: true, trackWhatsApp: true
  },

  /* ------------------------------------------------------------------- mode */
  // WHO sends the data to GA4 -- the anti-double-counting switch.
  //   'gtm' (default) -- we are a pure data layer; the merchant's GTM container
  //     does all the sending. We NEVER emit GA4 ourselves in this mode.
  //   'ga4'           -- no GTM: we load gtag.js and send GA4 events ourselves
  //     from `ga4MeasurementId` (see core/ga4direct.js, which additionally
  //     refuses to start if any Google tag is already on the page).
  mode: 'gtm',

  /* -------------------------------------------------------------- transport */
  // Where emitted events GO.
  //   'dataLayer' (default) -- window.dataLayer push; GTM (browser) consumes.
  //   'endpoint'            -- POST each event as JSON to `url` (an sGTM/Stape
  //     or relay endpoint). This is the ONLY path inside a Shopify APP pixel:
  //     the strict sandbox cannot load external JS, so GTM can never run
  //     there -- events must leave via fetch to a server-side container.
  //   'ga4mp'               -- POST the GA4 Measurement Protocol (see
  //     core/ga4mp.js). Needs `ga4MeasurementId` + `ga4.apiSecret` and NO
  //     server of any kind: this is how a checkout pixel reports purchases
  //     for a merchant who only has a GA4 property.
  transport: { mode: 'dataLayer', url: '' },

  /* ---------------------------------------------------------- GA4 (direct) */
  // Only used by the 'ga4mp' transport / 'ga4' mode.
  //   apiSecret -- GA4 Admin -> Data Streams -> your stream -> Measurement
  //     Protocol API secrets -> Create. Without it GA4 silently drops the hit.
  //   mpUrl     -- override the collection host (e.g. a tagging server that
  //     accepts /mp/collect). Default https://www.google-analytics.com.
  ga4: { apiSecret: '', mpUrl: '' },

  /* --------------------------------------------------------------- identity */
  // OPTIONAL. GA4 data is sent by GTM (the container's GA4 tags), NOT by this
  // value -- so a merchant only needs to set gtm.id. Left empty, session_id is
  // auto-detected from the _ga_<id> cookie on the storefront; set it only to
  // also stitch session_id inside the checkout pixel sandbox (advanced).
  ga4MeasurementId: ''         // e.g. 'G-XXXXXXX' (optional; session_id stitching only)
};

DL.config = null;

/** Deep-merge user config over defaults. Arrays are replaced, not merged. */
DL.buildConfig = function (user) {
  function merge(base, over) {
    var out = {};
    var k;
    for (k in base) out[k] = base[k];
    for (k in over) {
      if (
        over[k] && typeof over[k] === 'object' && !Array.isArray(over[k]) &&
        base[k] && typeof base[k] === 'object' && !Array.isArray(base[k])
      ) {
        out[k] = merge(base[k], over[k]);
      } else {
        out[k] = over[k];
      }
    }
    return out;
  }
  DL.config = merge(DL.defaults, user || {});
  if (DL.destinations) DL.destinations.clear();   // matrix is re-resolved per config
  if (!DL.config.gtm.id) {
    DL.debug.warn('config.gtm.id missing -- GTM will not load');
  }
  // resolve auto flags once
  if (DL.config.browserPlatformBlocks === null) {
    // With a destinations matrix the answer is no longer "server or browser"
    // for the whole store -- it is per platform. Build browser blocks whenever
    // at least one platform is fed from the client.
    var matrix = DL.destinations && DL.destinations.all();
    if (matrix && Object.keys(matrix).length) {
      DL.config.browserPlatformBlocks = false;
      for (var name in matrix) {
        if (DL.destinations.sendsClient(name)) { DL.config.browserPlatformBlocks = true; break; }
      }
    } else {
      DL.config.browserPlatformBlocks = !DL.config.serverSide.enabled;
    }
  }
  return DL.config;
};

/* ============================================================================
 * core/normalize.js --  the anti-mismatch core
 *
 * THE RULES (breaking any of these caused a real client bug):
 *   1. Money enters as {amount, unit, currency}. Adapters DECLARE their unit
 *      ('cents' | 'decimal'); a hand-written /100 anywhere else is a bug.
 *      (Cart AJAX + Liquid give integer cents; the pixel gives decimal.)
 *   2. Currency is never hardcoded and never guessed.
 *   3. Every id becomes String() at normalize time. Cart AJAX ids are
 *      integers, pixel ids are strings -- unmerged they become two different
 *      items in GA4.
 *   4. "" fallbacks are banned: a missing value means the KEY IS OMITTED.
 *   5. Rounding to 2dp happens exactly once, at emit time (JS: 19.99*3 =
 *      59.97000000000001).
 *   6. A key's type never changes between events.
 * ========================================================================== */

DL.normalize = (function () {
  var get = function (o, p, f) { return DL.safe.get(o, p, f); };

  /* ------------------------------------------------------------------ money */

  /**
   * money(raw, unit) -> decimal number | undefined
   * unit: 'cents' | 'decimal'. Anything unparseable -> undefined (never 0 --
   * a silent 0 is how value:null bugs are born).
   */
  function money(raw, unit) {
    if (raw == null || raw === '') return undefined;
    var n = typeof raw === 'number' ? raw : parseFloat(raw);
    if (isNaN(n) || !isFinite(n)) return undefined;
    if (unit === 'cents') return n / 100;
    if (unit === 'decimal') return n;
    DL.debug.error('normalize.money: adapter did not declare unit -- value refused');
    return undefined;
  }

  /** Round to 2dp for emission. Only call at the edge, never mid-calculation. */
  function round2(n) {
    if (typeof n !== 'number' || isNaN(n) || !isFinite(n)) return undefined;
    return Math.round((n + Number.EPSILON) * 100) / 100;
  }

  /* -------------------------------------------------------------------- ids */

  /**
   * Coerce any id to a clean string; strips Shopify GID prefixes so
   * 'gid://shopify/Product/123' and 123 and '123' all become '123'.
   * null/undefined -> undefined (key omitted), never ''.
   */
  function id(raw) {
    if (raw == null || raw === '') return undefined;
    var s = String(raw);
    if (s.indexOf('gid://') === 0) {
      var parts = s.split('/');
      s = parts[parts.length - 1];
      // strip querystring some GIDs carry
      var q = s.indexOf('?');
      if (q > -1) s = s.slice(0, q);
    }
    return s || undefined;
  }

  /** Non-empty trimmed string or undefined. Enforces rule 4. */
  function str(raw) {
    if (raw == null) return undefined;
    var s = String(raw).trim();
    return s === '' ? undefined : s;
  }

  /** Positive integer or undefined. */
  function int(raw) {
    if (raw == null || raw === '') return undefined;
    var n = typeof raw === 'number' ? raw : parseInt(raw, 10);
    if (isNaN(n) || !isFinite(n)) return undefined;
    return Math.round(n);
  }

  /** Assign key only when value is defined -- rule 4 as a helper. */
  function put(obj, key, val) {
    if (val !== undefined) obj[key] = val;
    return obj;
  }

  /* ------------------------------------------------------------------ items */

  /**
   * Canonical item (produced by adapters):
   * {
   *   productId, variantId, sku,          // raw, any type -- coerced here
   *   name, brand, category, variantTitle,
   *   price: { amount, unit, currency },
   *   quantity, index, listId, listName
   * }
   *
   * -> GA4 item. item_id follows config.itemIdSource with fallback so it is
   * never empty; returns null if no usable id exists at all (caller must
   * then drop the item, logged).
   */
  function item(c, cfg) {
    cfg = cfg || DL.config;
    var ids = {
      sku: id(c.sku),
      variant_id: id(c.variantId),
      product_id: id(c.productId)
    };
    // Merchant Center offer id as generated by Google's Shopify app:
    // shopify_<COUNTRY>_<productId>_<variantId>. Only usable when BOTH ids are
    // present; otherwise fall through to the normal sources below.
    if (ids.product_id && ids.variant_id) {
      ids.shopify_feed = 'shopify_' + (cfg.feedCountry || 'US') + '_' +
        ids.product_id + '_' + ids.variant_id;
    }
    var primary = ids[cfg.itemIdSource] || ids[cfg.itemIdFallback] ||
      ids.variant_id || ids.product_id || ids.sku;
    if (!primary) {
      DL.debug.warn('normalize.item: no usable id -- item dropped', c);
      return null;
    }
    var out = { item_id: primary, quantity: int(c.quantity) || 1 };
    put(out, 'item_name', str(c.name));
    put(out, 'item_brand', str(c.brand));
    put(out, 'item_category', str(c.category));
    put(out, 'item_variant', str(c.variantTitle));
    put(out, 'price', round2(money(get(c, 'price.amount'), get(c, 'price.unit'))));
    put(out, 'index', int(c.index));
    put(out, 'item_list_id', str(c.listId));
    put(out, 'item_list_name', str(c.listName));
    return out;
  }

  /** Map + filter a canonical item array. Always returns an array (rule: never null). */
  function items(list, cfg) {
    if (!Array.isArray(list)) return [];
    var out = [];
    for (var i = 0; i < list.length; i++) {
      var it = item(list[i], cfg);
      if (it) out.push(it);
    }
    return out;
  }

  /** Event-level helper arrays: null-filtered, always string[], never [null]. */
  function idArray(list, key) {
    if (!Array.isArray(list)) return [];
    var out = [];
    for (var i = 0; i < list.length; i++) {
      var v = id(list[i] && list[i][key]);
      if (v !== undefined) out.push(v);
    }
    return out;
  }

  return {
    money: money, round2: round2, id: id, str: str, int: int, put: put,
    item: item, items: items, idArray: idArray
  };
})();

/* ============================================================================
 * core/schema.js -- the GA4 event/param contract + runtime validator
 *
 * The contract is enforced at runtime: an event that violates it is NOT
 * pushed (and the drop is logged with the reason). Wrong data in GA4 is
 * worse than missing data -- missing data you notice, wrong data you bill
 * decisions on.
 * ========================================================================== */

DL.schema = (function () {
  /* Per-event declaration:
   *   consent : which CMv2 category must be granted before this event may
   *             leave the theme queue ('analytics_storage' | 'ad_storage')
   *   needsItems / needsValue / needsTransaction : validation guards
   */
  var EVENTS = {
    page_view:             { consent: 'analytics_storage' },
    view_item:             { consent: 'analytics_storage', needsItems: true, needsValue: true },
    view_item_list:        { consent: 'analytics_storage', needsItems: true },
    select_item:           { consent: 'analytics_storage', needsItems: true },
    add_to_cart:           { consent: 'analytics_storage', needsItems: true, needsValue: true },
    remove_from_cart:      { consent: 'analytics_storage', needsItems: true, needsValue: true },
    view_cart:             { consent: 'analytics_storage', needsValue: true },
    search:                { consent: 'analytics_storage' },
    view_search_results:   { consent: 'analytics_storage' },
    view_promotion:        { consent: 'analytics_storage' },
    select_promotion:      { consent: 'analytics_storage' },
    begin_checkout:        { consent: 'analytics_storage', needsItems: true, needsValue: true },
    checkout_contact_info_submitted: { consent: 'analytics_storage', needsItems: true },
    checkout_address_info_submitted: { consent: 'analytics_storage', needsItems: true },
    add_shipping_info:     { consent: 'analytics_storage', needsItems: true, needsValue: true },
    add_payment_info:      { consent: 'analytics_storage', needsItems: true, needsValue: true },
    purchase:              { consent: 'analytics_storage', needsItems: true, needsValue: true, needsTransaction: true },
    generate_lead:         { consent: 'analytics_storage' },
    sign_up:               { consent: 'analytics_storage' },
    login:                 { consent: 'analytics_storage' },
    form_submit:           { consent: 'analytics_storage' },
    consent_state:         { consent: null },   // meta event, always allowed
    context_ready:         { consent: null }
  };

  function known(name) { return Object.prototype.hasOwnProperty.call(EVENTS, name); }
  function spec(name) { return EVENTS[name] || {}; }

  /* -------------------------------------------------------------- validator */

  function isNum(v) { return typeof v === 'number' && !isNaN(v) && isFinite(v); }
  function isStr(v) { return typeof v === 'string'; }
  function isInt(v) { return isNum(v) && v === Math.round(v); }

  /**
   * validate(evt) -> { ok: true } | { ok: false, errors: [...] }
   * Checks the three hard rules + per-event guards. Never throws.
   */
  function validate(evt) {
    var errors = [];
    var s = spec(evt.event);

    if (!isStr(evt.event) || evt.event === '') errors.push('event name missing');
    if (!known(evt.event)) errors.push('unknown event "' + evt.event + '"');

    // rule: no "" anywhere at top level
    for (var k in evt) {
      if (evt[k] === '') errors.push('empty-string value at key "' + k + '" (banned -- omit the key)');
      if (evt[k] === null && k !== 'ecommerce') errors.push('null value at key "' + k + '" (banned -- omit the key)');
    }

    var ec = evt.ecommerce;
    if (ec) {
      if (ec.items !== undefined && !Array.isArray(ec.items)) errors.push('ecommerce.items must be an array');
      if (ec.currency !== undefined && (!isStr(ec.currency) || ec.currency.length !== 3)) {
        errors.push('ecommerce.currency must be a 3-letter string');
      }
      if (ec.value !== undefined && !isNum(ec.value)) errors.push('ecommerce.value must be a number');
      if (ec.transaction_id !== undefined && (!isStr(ec.transaction_id) || ec.transaction_id === '')) {
        errors.push('ecommerce.transaction_id must be a non-empty string');
      }
      if (Array.isArray(ec.items)) {
        for (var i = 0; i < ec.items.length; i++) {
          var it = ec.items[i];
          if (!it || !isStr(it.item_id) || it.item_id === '') errors.push('items[' + i + '].item_id must be a non-empty string');
          if (it && it.price !== undefined && !isNum(it.price)) errors.push('items[' + i + '].price must be a number');
          if (it && it.quantity !== undefined && !isInt(it.quantity)) errors.push('items[' + i + '].quantity must be an integer');
          for (var ik in it) {
            if (it[ik] === '' || it[ik] === null) errors.push('items[' + i + '].' + ik + ' is empty/null (banned -- omit the key)');
          }
        }
      }

      // per-event guards -- these block, not warn
      if (s.needsItems && (!Array.isArray(ec.items) || ec.items.length === 0)) {
        errors.push(evt.event + ' requires non-empty ecommerce.items');
      }
      if (s.needsValue) {
        if (!isNum(ec.value)) errors.push(evt.event + ' requires numeric ecommerce.value');
        if (!isStr(ec.currency)) errors.push(evt.event + ' requires ecommerce.currency');
      }
      if (s.needsTransaction) {
        if (!isStr(ec.transaction_id) || ec.transaction_id === '') {
          errors.push('purchase requires non-empty transaction_id');
        }
        // value 0 is valid: a 100%-off / free order with a real transaction_id is a
        // real conversion (GA4/Ads/Meta accept $0 purchases). Only negative/junk blocks.
        if (isNum(ec.value) && ec.value < 0) {
          errors.push('purchase value must be >= 0 (got ' + ec.value + ')');
        }
      }
    } else if (s.needsItems || s.needsValue || s.needsTransaction) {
      errors.push(evt.event + ' requires an ecommerce object');
    }

    return errors.length ? { ok: false, errors: errors } : { ok: true };
  }

  return { known: known, spec: spec, validate: validate, EVENTS: EVENTS };
})();

/* ============================================================================
 * core/identity.js -- GA4 client_id / session_id, event_id, user_data hashing
 *
 * The REAL GA4 client id lives in the _ga cookie ("GA1.1.111111.2222222").
 * Shopify's own event.clientId is NOT it (bug C7 in the reference script) --
 * forwarding the wrong one orphans every checkout purchase from its
 * storefront session in sGTM.
 * ========================================================================== */

DL.identity = (function () {
  /** "GA1.1.1111111111.2222222222" -> "1111111111.2222222222" */
  function parseGaCookie(raw) {
    if (!raw || typeof raw !== 'string') return undefined;
    var parts = raw.split('.');
    if (parts.length < 4) return undefined;
    return parts[parts.length - 2] + '.' + parts[parts.length - 1];
  }

  /** "GS1.1.1699999999.5.1.1699999999.60.0.0" -> "1699999999" (session id) */
  function parseSessionCookie(raw) {
    if (!raw || typeof raw !== 'string') return undefined;
    var parts = raw.split('.');
    if (parts.length < 3) return undefined;
    return parts[2] || undefined;
  }

  /** Theme side: read document.cookie synchronously. */
  function readCookieSync(name) {
    if (typeof document === 'undefined' || !document.cookie) return undefined;
    var m = document.cookie.match(new RegExp('(?:^|;\\s*)' + name.replace(/([.*+?^${}()|[\]\\])/g, '\\$1') + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : undefined;
  }

  function clientIdFromCookies() {
    return parseGaCookie(readCookieSync('_ga'));
  }

  function sessionIdFromCookies(measurementId) {
    // If a measurement id is configured, read its exact _ga_<suffix> cookie.
    if (measurementId) {
      var suffix = String(measurementId).replace(/^G-/, '');
      return parseSessionCookie(readCookieSync('_ga_' + suffix));
    }
    // Otherwise AUTO-DETECT (top frame only): GA4 sending lives in GTM, so the
    // merchant never has to configure the GA4 id -- we scan for the _ga_<id>
    // session cookie GA4 wrote. (Sandbox can't enumerate cookies, so the pixel
    // simply omits session_id when the id is unset -- client_id still forwards.)
    if (typeof document !== 'undefined' && document.cookie) {
      var m = document.cookie.match(/(?:^|;\s*)_ga_[A-Z0-9]+=([^;]*)/);
      if (m) return parseSessionCookie(decodeURIComponent(m[1]));
    }
    return undefined;
  }

  /* --------------------------------------------------------------- event_id */

  /** uuid v4 (crypto if available, Math.random fallback). */
  function uuid() {
    var cr = (typeof crypto !== 'undefined' && crypto.getRandomValues) ? crypto : null;
    if (cr && cr.randomUUID) return cr.randomUUID();
    var bytes = new Array(16);
    if (cr) {
      var buf = new Uint8Array(16);
      cr.getRandomValues(buf);
      for (var i = 0; i < 16; i++) bytes[i] = buf[i];
    } else {
      for (var j = 0; j < 16; j++) bytes[j] = Math.floor(Math.random() * 256);
    }
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    var hex = [];
    for (var k = 0; k < 16; k++) hex.push((bytes[k] + 256).toString(16).slice(1));
    return hex.slice(0, 4).join('') + '-' + hex.slice(4, 6).join('') + '-' +
      hex.slice(6, 8).join('') + '-' + hex.slice(8, 10).join('') + '-' + hex.slice(10).join('');
  }

  /**
   * event_id:
   *  - purchase -> DETERMINISTIC from order id ("pur_<orderId>") so browser,
   *    theme, sGTM and Meta CAPI all dedupe on the same key, even across a
   *    thank-you page refresh.
   *  - everything else -> uuid v4.
   */
  function eventId(eventName, orderId) {
    if (eventName === 'purchase' && orderId) {
      return 'pur_' + DL.normalize.id(orderId);
    }
    return uuid();
  }

  /* -------------------------------------------------------------- user_data */

  /** SHA-256 -> lowercase hex. Async (SubtleCrypto); undefined when unavailable. */
  function sha256(text) {
    if (typeof crypto === 'undefined' || !crypto.subtle || !text) {
      return Promise.resolve(undefined);
    }
    var data = new TextEncoder().encode(text);
    return crypto.subtle.digest('SHA-256', data).then(function (buf) {
      var arr = new Uint8Array(buf);
      var hex = '';
      for (var i = 0; i < arr.length; i++) hex += (arr[i] + 256).toString(16).slice(1);
      return hex;
    }).catch(function () { return undefined; });
  }

  /** Normalize per Google/Meta spec before hashing. */
  function normalizeEmail(email) {
    var e = DL.normalize.str(email);
    return e ? e.toLowerCase() : undefined;
  }
  function normalizePhone(phone) {
    var p = DL.normalize.str(phone);
    if (!p) return undefined;
    p = p.replace(/[^0-9+]/g, '');
    return p || undefined;
  }
  /** Names / street / city / region / country: trim, lowercase, collapse spaces. */
  function normalizeText(v) {
    var s = DL.normalize.str(v);
    return s ? s.toLowerCase().replace(/\s+/g, ' ') : undefined;
  }

  /**
   * buildUserData(raw) -> Promise<user_data | undefined>
   * Google Enhanced Conversions spec: email, phone, first_name, last_name, street
   * are SHA-256 hashed; city, region, postal_code, country are sent in the clear
   * (Google uses them plain for matching). NEVER emits plaintext email/phone/name/
   * street (bug C5). Postal keeps its case (UK codes are case-significant).
   */
  function buildUserData(raw) {
    if (!raw) return Promise.resolve(undefined);
    var email  = normalizeEmail(raw.email);
    var phone  = normalizePhone(raw.phone);
    var fname  = normalizeText(raw.firstName);
    var lname  = normalizeText(raw.lastName);
    var street = normalizeText(raw.street);
    var city    = normalizeText(raw.city);
    var region  = normalizeText(raw.region);
    var country = normalizeText(raw.country);
    var postal  = DL.normalize.str(raw.postalCode);
    return Promise.all([
      sha256(email), sha256(phone), sha256(fname), sha256(lname), sha256(street)
    ]).then(function (r) {
      var out = {};
      DL.normalize.put(out, 'sha256_email', r[0]);
      DL.normalize.put(out, 'sha256_phone', r[1]);
      DL.normalize.put(out, 'sha256_first_name', r[2]);
      DL.normalize.put(out, 'sha256_last_name', r[3]);
      DL.normalize.put(out, 'sha256_street', r[4]);
      DL.normalize.put(out, 'city', city);
      DL.normalize.put(out, 'region', region);
      DL.normalize.put(out, 'postal_code', postal);
      DL.normalize.put(out, 'country', country);
      for (var k in out) { if (out.hasOwnProperty(k)) return out; }
      return undefined;
    });
  }

  /**
   * buildUserDataRaw(raw) -> normalized-but-UNHASHED user_data for the OFFICIAL
   * browser vendor mechanisms -- Google's User-Provided Data variable and Meta's
   * Advanced Matching. Those SDKs hash in-browser before the network request, so
   * plaintext never leaves the page. Synchronous. Emitted as evt.user_data_raw
   * (separate from the SHA-256 evt.user_data, which stays for the sGTM path).
   */
  function buildUserDataRaw(raw) {
    if (!raw) return undefined;
    var out = {};
    DL.normalize.put(out, 'email', normalizeEmail(raw.email));
    DL.normalize.put(out, 'phone', normalizePhone(raw.phone));
    DL.normalize.put(out, 'first_name', normalizeText(raw.firstName));
    DL.normalize.put(out, 'last_name', normalizeText(raw.lastName));
    DL.normalize.put(out, 'street', normalizeText(raw.street));
    DL.normalize.put(out, 'city', normalizeText(raw.city));
    DL.normalize.put(out, 'region', normalizeText(raw.region));
    DL.normalize.put(out, 'postal_code', DL.normalize.str(raw.postalCode));
    DL.normalize.put(out, 'country', normalizeText(raw.country));
    for (var k in out) { if (out.hasOwnProperty(k)) return out; }
    return undefined;
  }


  /* ------------------------------------------------------------- click ids */
  // Ad platforms match a server-side conversion back to the click that caused
  // it. Without these, a Conversions API event is nearly unattributable.
  //
  // They matter MORE for the checkout pixel than anywhere else: its fetch goes
  // out from Shopify's sandbox, which is a different origin, so the shop's
  // first-party cookies are NOT attached to the request. The server can only
  // see what we put in the body -- hence we read them and forward them.
  var CLICK_COOKIES = {
    gclid: '_gcl_aw',      // Google Ads  ("GCL.<ts>.<gclid>")
    gclsrc: '_gcl_dc',     // Google Ads (Display/DV360 click)
    fbp: '_fbp',           // Meta browser id
    fbc: '_fbc',           // Meta click id
    ttclid: 'ttclid',      // TikTok click id
    ttp: '_ttp',           // TikTok browser id
    scid: '_scid',         // Snapchat
    epik: '_epik',         // Pinterest
    li_fat_id: 'li_fat_id' // LinkedIn
  };

  var clickIds = {};

  /** "GCL.1699999999.Cj0KCQ..." -> the click id itself. */
  function unwrapGcl(raw) {
    if (!raw) return undefined;
    var parts = String(raw).split('.');
    return parts.length >= 3 ? parts.slice(2).join('.') : raw;
  }

  function setClickId(name, raw) {
    if (!raw) return;
    var value = (name === 'gclid' || name === 'gclsrc') ? unwrapGcl(raw) : raw;
    if (value) clickIds[name] = value;
  }

  /** Theme side: cookies are readable synchronously, and the URL is authoritative
   *  for a click that just happened (the cookie may not be written yet). */
  function readClickIds() {
    for (var name in CLICK_COOKIES) setClickId(name, readCookieSync(CLICK_COOKIES[name]));
    if (typeof document !== 'undefined' && document.location && document.location.search) {
      var q = document.location.search;
      ['gclid', 'gbraid', 'wbraid', 'ttclid', 'fbclid', 'epik', 'li_fat_id'].forEach(function (k) {
        var m = q.match(new RegExp('[?&]' + k + '=([^&]+)'));
        if (m) clickIds[k] = decodeURIComponent(m[1]);
      });
    }
    return clickIds;
  }

  /** Pixel side: browser.cookie.get is async and reads the TOP frame. */
  function loadClickIds(browserApi) {
    if (!browserApi || !browserApi.cookie) return Promise.resolve(clickIds);
    var jobs = [];
    var names = Object.keys(CLICK_COOKIES);
    names.forEach(function (name) {
      jobs.push(
        browserApi.cookie.get(CLICK_COOKIES[name])
          .then(function (raw) { setClickId(name, raw); })
          .catch(function () { /* a missing cookie is normal */ })
      );
    });
    return Promise.all(jobs).then(function () { return clickIds; });
  }

  function getClickIds() {
    for (var k in clickIds) { if (clickIds.hasOwnProperty(k)) return clickIds; }
    return undefined;      // absent, never an empty object
  }

  return {
    parseGaCookie: parseGaCookie,
    parseSessionCookie: parseSessionCookie,
    clientIdFromCookies: clientIdFromCookies,
    sessionIdFromCookies: sessionIdFromCookies,
    uuid: uuid,
    eventId: eventId,
    buildUserData: buildUserData,
    buildUserDataRaw: buildUserDataRaw,
    normalizeEmail: normalizeEmail,
    normalizePhone: normalizePhone,
    readClickIds: readClickIds,
    loadClickIds: loadClickIds,
    getClickIds: getClickIds,
    _clickCookies: CLICK_COOKIES
  };
})();

/* ============================================================================
 * core/consent.js -- Customer Privacy reader + queue gate + CMv2 emitter
 *
 * Division of labour (from the plan -- do not blur it):
 *   - Banner UI               -> the CMP app's job. Never ours.
 *   - Gating the PIXEL        -> Shopify's job (permission config). Never ours.
 *   - Gating THEME JS         -> OUR job. Shopify gates nothing in the theme.
 *   - Region decisions        -> the CMP's job. We only read resulting state.
 *
 * Dual mode (config.consent.mode):
 *   'shopify' -- CMP syncs to Customer Privacy API; we read it and WE emit
 *               Consent Mode v2 default/update.
 *   'gtm'     -- client's GTM has a consent template; we emit NO CMv2 signal,
 *               only a consent_state dataLayer push (+ the queue gate).
 *   'auto'    -- Customer Privacy API present -> 'shopify', else 'gtm'.
 *
 * Verified platform facts baked in:
 *   - The API must be loaded first: window.Shopify.loadFeatures(
 *       [{name:'consent-tracking-api', version:'0.1'}], cb)
 *   - visitorConsentCollected fires ONLY on change -- a visitor who consented
 *     on a previous visit gets NO event. Initial state MUST be read at init
 *     or their events sit in the queue forever.
 * ========================================================================== */

DL.consent = (function () {
  var state = {
    mode: null,               // resolved 'shopify' | 'gtm'
    ready: false,             // initial state has been read
    analytics: false,
    marketing: false,
    queue: []                 // events waiting for consent
  };

  function api() {
    return DL.safe.get(window, 'Shopify.customerPrivacy');
  }

  /* ----------------------------------------------------------------- gating */

  /** May this event pass right now? (category from schema; null = always) */
  function allowed(eventName) {
    var cat = DL.schema.spec(eventName).consent;
    if (cat === null || cat === undefined) return true;
    if (!state.ready) return false;               // default deny before init
    if (cat === 'analytics_storage') return state.analytics;
    if (cat === 'ad_storage') return state.marketing;
    return false;
  }

  function enqueue(evt) {
    state.queue.push(evt);
    DL.debug.drop('consent-blocked (queued)', evt.event);
  }

  function flush() {
    if (!state.queue.length) return;
    var pending = state.queue;
    state.queue = [];
    for (var i = 0; i < pending.length; i++) {
      DL.emit.push(pending[i]);                    // re-enters the gate; still-blocked events re-queue
    }
  }

  /* ------------------------------------------------------------------- CMv2 */

  var gtag = function () { window.dataLayer = window.dataLayer || []; window.dataLayer.push(arguments); };

  function weOwnCmv2() {
    return state.mode === 'shopify' && DL.config.consent.owner === 'datalayer';
  }

  /** Emit CMv2 default BEFORE GTM loads. Call as early as possible.
   * Only runs in the owner==='datalayer' fallback; when a CMP template owns
   * consent it sets the default (and these advanced flags) itself. */
  function emitDefault() {
    if (!weOwnCmv2()) return;
    var c = DL.config.consent;
    var d = c.defaultState;
    // Consent Mode ADVANCED: redact ad ids + pass identifiers through URLs while
    // denied, so tags can load pre-consent and still calibrate modeling.
    if (c.adsDataRedaction) gtag('set', 'ads_data_redaction', true);
    if (c.urlPassthrough) gtag('set', 'url_passthrough', true);
    gtag('consent', 'default', {
      ad_storage: d.ad_storage,
      ad_user_data: d.ad_user_data,
      ad_personalization: d.ad_personalization,
      analytics_storage: d.analytics_storage,
      wait_for_update: c.waitForUpdateMs
    });
  }

  function emitUpdate() {
    if (!weOwnCmv2()) return;
    gtag('consent', 'update', {
      ad_storage: state.marketing ? 'granted' : 'denied',
      ad_user_data: state.marketing ? 'granted' : 'denied',
      ad_personalization: state.marketing ? 'granted' : 'denied',
      analytics_storage: state.analytics ? 'granted' : 'denied'
    });
  }

  /** Always pushed (both modes) so GTM consent templates can consume it. */
  function pushConsentState() {
    DL.emit.push({
      event: 'consent_state',
      consent_analytics: state.analytics,
      consent_marketing: state.marketing,
      consent_mode: state.mode
    });
  }

  /* --------------------------------------------- foreign CMv2 signal detect */
  // Blackwing failed silently because two parties raced on consent signals.
  // We detect a consent push we didn't make and WARN -- never auto-silence.
  var ownPushes = 0;
  function watchForeignSignals() {
    var dl = window.dataLayer = window.dataLayer || [];
    var origPush = dl.push;
    dl.push = function () {
      var arg = arguments[0];
      // gtag('consent',...) arrives as an Arguments object
      var isConsent = arg && typeof arg === 'object' && arg[0] === 'consent';
      if (isConsent) {
        if (ownPushes > 0) { ownPushes--; }
        else if (weOwnCmv2()) {
          DL.debug.warn(
            'FOREIGN Consent Mode signal detected (not ours). Two consent owners cause silent data loss. ' +
            'Either set consent.owner="cmp" or disable the CMP\'s Google Consent Mode toggle.'
          );
        }
      }
      return origPush.apply(dl, arguments);
    };
    // count our own pushes so we don't warn on ourselves
    var origGtagPush = gtag;
    gtag = function () { ownPushes++; return origGtagPush.apply(null, arguments); };
  }

  /* ------------------------------------------------------------------- init */

  function readState(cp) {
    try {
      state.analytics = !!cp.analyticsProcessingAllowed();
      state.marketing = !!cp.marketingAllowed();
    } catch (err) {
      DL.debug.error('customerPrivacy read failed -- staying default-deny', err);
      state.analytics = false;
      state.marketing = false;
    }
    state.ready = true;
  }

  function onChanged(detail) {
    state.analytics = !!(detail && detail.analyticsAllowed);
    state.marketing = !!(detail && detail.marketingAllowed);
    state.ready = true;
    emitUpdate();
    pushConsentState();
    flush();
  }

  /**
   * init() -- theme side only. Resolves mode, emits CMv2 default, loads the
   * Customer Privacy API, reads INITIAL state (mandatory -- see header),
   * subscribes to changes.
   */
  function init() {
    var cfgMode = DL.config.consent.mode;
    watchForeignSignals();

    var hasShopifyPrivacy = !!DL.safe.get(window, 'Shopify.loadFeatures');
    state.mode = cfgMode === 'auto' ? (hasShopifyPrivacy ? 'shopify' : 'gtm') : cfgMode;

    emitDefault(); // before GTM -- order matters

    // change listener (fires on CHANGE only)
    if (typeof document !== 'undefined') {
      document.addEventListener('visitorConsentCollected', function (e) {
        onChanged(e && e.detail);
      });
    }

    if (state.mode === 'shopify' && hasShopifyPrivacy) {
      try {
        window.Shopify.loadFeatures(
          [{ name: 'consent-tracking-api', version: '0.1' }],
          function (err) {
            if (err) {
              DL.debug.error('consent-tracking-api load failed -- staying default-deny', err);
              return;
            }
            var cp = api();
            if (!cp) return;
            readState(cp);       // <- initial state: returning consented visitors
            emitUpdate();
            pushConsentState();
            flush();
          }
        );
      } catch (err) {
        DL.debug.error('loadFeatures threw -- staying default-deny', err);
      }
    } else {
      // 'gtm' mode: the CMP/GTM template owns signals. We still gate the
      // queue on Customer Privacy if it happens to exist; otherwise we have
      // no truth source and stay default-deny until visitorConsentCollected.
      var cp = api();
      if (cp) { readState(cp); pushConsentState(); flush(); }
      else {
        DL.debug.log('consent mode "gtm": no Customer Privacy API -- default-deny until visitorConsentCollected fires');
      }
    }
  }

  /* Test hook */
  function _reset() {
    state.mode = null; state.ready = false;
    state.analytics = false; state.marketing = false; state.queue = [];
  }

  return {
    init: init, allowed: allowed, enqueue: enqueue, flush: flush,
    pushConsentState: pushConsentState, onChanged: onChanged,
    state: state, _reset: _reset
  };
})();

/* ============================================================================
 * core/destinations.js -- WHO gets each event, and from WHICH side
 *
 * One event can legitimately reach many platforms (GA4, Google Ads, Meta,
 * TikTok...), and each of them can be fed from the browser, from a server, or
 * both. That freedom is exactly how duplicate conversions get created, so the
 * matrix is enforced in ONE place instead of being a per-tag decision:
 *
 *   side: 'client'  -- browser only (GTM tag / gtag / browser pixel)
 *   side: 'server'  -- server only (sGTM tag, Conversions API, relay)
 *   side: 'both'    -- ONLY for platforms that officially de-duplicate the two
 *                     copies by event id. We send the SAME event_id on both
 *                     sides, which is what the vendor matches on.
 *
 * Vendors that de-duplicate by event id (documented):
 *   Meta       -- `eventID` on the browser pixel === `event_id` on CAPI
 *   TikTok     -- `event_id` on Pixel === Events API
 *   Pinterest / Snapchat / LinkedIn -- same pattern in their Conversions APIs
 *
 * Vendors that do NOT (so 'both' is refused, loudly):
 *   GA4        -- two hits = two events; the only correct answer is one sender
 *   Google Ads -- a browser conversion + a server conversion double-count
 *
 * This module decides nothing about HOW an event travels (that's transport in
 * emit.js); it only answers "should this platform hear it from this side?".
 * ========================================================================== */

DL.destinations = (function () {
  /* Platforms whose own API matches our event_id across the two sides. */
  var DEDUPES_BY_EVENT_ID = {
    meta: true, tiktok: true, pinterest: true, snapchat: true, linkedin: true
  };

  /* Aliases so old config (`platforms: ['facebook', ...]`) keeps working. */
  var ALIAS = { facebook: 'meta', fb: 'meta', gads: 'google_ads', ga: 'ga4' };

  function canonical(name) {
    var n = String(name || '').toLowerCase();
    return ALIAS[n] || n;
  }

  /** Is a server-side path configured at all? */
  function hasServer() {
    var tr = DL.safe.get(DL, 'config.transport') || {};
    return !!(tr.mode === 'endpoint' && tr.url) || !!DL.safe.get(DL, 'config.serverSide.relayUrl');
  }

  /**
   * resolve() -- the enforced matrix. Returns
   *   { meta: { enabled, side, ids... }, ... }
   * with every illegal combination already corrected and logged.
   */
  function resolve() {
    var out = {};
    var conf = DL.safe.get(DL, 'config.destinations') || {};
    var name, d, side;

    for (name in conf) {
      d = conf[name] || {};
      if (!d.enabled) continue;
      var key = canonical(name);
      side = String(d.side || 'client').toLowerCase();

      if (side === 'both' && !DEDUPES_BY_EVENT_ID[key]) {
        // The honest failure mode: silently allowing this is how a store ends
        // up reporting twice the revenue it made.
        side = hasServer() ? 'server' : 'client';
        DL.debug.warn(
          key + ': "both" is not possible -- it has no event-id de-duplication. ' +
          'Falling back to ' + side + '-side only so conversions are not counted twice.'
        );
      }
      if (side === 'server' && !hasServer()) {
        side = 'client';
        DL.debug.warn(
          key + ': set to server-side but no server endpoint/relay is configured -- ' +
          'using client-side so data is not lost.'
        );
      }
      if (side !== 'client' && side !== 'server' && side !== 'both') side = 'client';

      out[key] = { enabled: true, side: side };
      for (var k in d) { if (k !== 'enabled' && k !== 'side') out[key][k] = d[k]; }
    }
    return out;
  }

  /** Memoized per config build (buildConfig clears it). */
  var cache = null;
  function all() {
    if (!cache) cache = resolve();
    return cache;
  }
  function clear() { cache = null; }

  function sideOf(name) {
    var d = all()[canonical(name)];
    return d ? d.side : null;
  }
  function sendsClient(name) { var s = sideOf(name); return s === 'client' || s === 'both'; }
  function sendsServer(name) { var s = sideOf(name); return s === 'server' || s === 'both'; }

  /** Names the SERVER is responsible for -- travels with each server payload so
   *  one sGTM/relay config can serve every store without its own settings. */
  function serverList() {
    var list = [];
    var map = all();
    for (var k in map) { if (sendsServer(k)) list.push(k); }
    return list.sort();
  }

  return {
    all: all, clear: clear, canonical: canonical, sideOf: sideOf,
    sendsClient: sendsClient, sendsServer: sendsServer, serverList: serverList,
    hasServer: hasServer, _dedupes: DEDUPES_BY_EVENT_ID
  };
})();

/* ============================================================================
 * core/ga4direct.js -- GA4 without GTM (browser / theme side)
 *
 * For merchants who have NO Google Tag Manager: we load gtag.js ourselves and
 * forward every emitted event straight to their GA4 property.
 *
 * DOUBLE-COUNTING GUARD (the whole point):
 *   - Only ever runs when `config.mode === 'ga4'`. In the default 'gtm' mode we
 *     stay a pure data layer and GTM does the sending -- we never send GA4 too.
 *   - If a Google tag for the SAME measurement id is already on the page
 *     (window.google_tag_data / an existing gtag script), we refuse to load a
 *     second one and log why. One property, one sender.
 * ========================================================================== */

DL.ga4direct = (function () {
  var started = false;
  var mid = '';

  function gtag() {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(arguments);
  }

  /** Already a Google tag for this measurement id on the page? */
  function alreadyPresent(id) {
    if (typeof document === 'undefined') return false;
    var s = document.getElementsByTagName('script');
    for (var i = 0; i < s.length; i++) {
      var src = s[i].src || '';
      if (src.indexOf('googletagmanager.com/gtag/js') !== -1 && src.indexOf(id) !== -1) return true;
      // a GTM container may itself load GA4 for this id -- leave sending to it
      if (src.indexOf('googletagmanager.com/gtm.js') !== -1) return true;
    }
    return false;
  }

  /** Load gtag.js once. Safe to call repeatedly. */
  function init() {
    if (started) return true;
    var cfg = DL.config || {};
    if (cfg.mode !== 'ga4') return false;                 // GTM mode: never send
    mid = cfg.ga4MeasurementId || '';
    if (!mid) {
      DL.debug.warn('mode "ga4" needs ga4MeasurementId -- nothing will be sent');
      return false;
    }
    if (alreadyPresent(mid)) {
      DL.debug.warn('a Google tag is already on the page -- NOT loading a second GA4 sender (prevents double counting)');
      return false;
    }
    if (typeof document === 'undefined') return false;    // pixel sandbox: server handles GA4

    var s = document.createElement('script');
    s.async = true;
    s.src = (cfg.gtm && cfg.gtm.loaderUrl ? cfg.gtm.loaderUrl.replace(/gtm\.js$/, 'gtag/js') :
      'https://www.googletagmanager.com/gtag/js') + '?id=' + encodeURIComponent(mid);
    var first = document.getElementsByTagName('script')[0];
    if (first && first.parentNode) first.parentNode.insertBefore(s, first);
    else document.head.appendChild(s);

    gtag('js', new Date());
    // send_page_view false: our own page_view event carries the right context
    gtag('config', mid, { send_page_view: false });
    started = true;
    DL.debug.log('GA4 direct mode active for', mid);
    return true;
  }

  /**
   * send(evt) -- forward one canonical event to GA4 as a gtag event.
   * Called from emit.push AFTER all guards (consent, dedup, validation).
   */
  function send(evt) {
    if (!DL.config || DL.config.mode !== 'ga4') return false;
    if (!started && !init()) return false;
    var params = {};
    var ec = evt.ecommerce;
    if (ec) {
      if (ec.currency) params.currency = ec.currency;
      if (ec.value != null) params.value = ec.value;
      if (ec.transaction_id) params.transaction_id = ec.transaction_id;
      if (ec.coupon) params.coupon = ec.coupon;
      if (ec.shipping != null) params.shipping = ec.shipping;
      if (ec.tax != null) params.tax = ec.tax;
      if (ec.items) params.items = ec.items;
    }
    if (evt.search_term) params.search_term = evt.search_term;
    if (evt.event_id) params.event_id = evt.event_id;
    try {
      gtag('event', evt.event, params);
    } catch (e) {
      DL.debug.error('gtag send failed', e);
      return false;
    }
    return true;
  }

  return { init: init, send: send, _alreadyPresent: alreadyPresent };
})();

/* ============================================================================
 * core/ga4mp.js -- GA4 Measurement Protocol sender (no GTM, no server needed)
 *
 * This is what makes the product work for merchants who have NOTHING but a GA4
 * property. It runs where gtag.js CAN'T: Shopify's strict-sandbox app pixel has
 * no `document`, so no script can be loaded -- but `fetch` is allowed, and the
 * Measurement Protocol is a plain HTTPS POST.
 *
 *   POST {base}/mp/collect?measurement_id=G-XXX&api_secret=SECRET
 *   { client_id, events: [{ name, params }] }
 *
 * `base` defaults to google-analytics.com; set config.ga4.mpUrl to a tagging
 * server instead (same protocol, first-party domain).
 *
 * Double counting: only ever called from emit.push in mode 'ga4' -- the same
 * single-destination switch that stops GTM and us from both sending.
 * ========================================================================== */

DL.ga4mp = (function () {
  var DEFAULT_BASE = 'https://www.google-analytics.com';

  function endpointUrl() {
    var cfg = DL.config || {};
    var ga4 = cfg.ga4 || {};
    var mid = cfg.ga4MeasurementId || '';
    if (!mid) return '';
    if (!ga4.apiSecret) return '';        // MP without a secret is silently dropped by GA4
    var base = (ga4.mpUrl || DEFAULT_BASE).replace(/\/+$/, '');
    return base + '/mp/collect?measurement_id=' + encodeURIComponent(mid) +
      '&api_secret=' + encodeURIComponent(ga4.apiSecret);
  }

  /** canonical event -> GA4 MP event params */
  function params(evt) {
    var p = {};
    var ec = evt.ecommerce;
    if (ec) {
      if (ec.currency) p.currency = ec.currency;
      if (ec.value != null) p.value = ec.value;
      if (ec.transaction_id) p.transaction_id = ec.transaction_id;
      if (ec.coupon) p.coupon = ec.coupon;
      if (ec.shipping != null) p.shipping = ec.shipping;
      if (ec.tax != null) p.tax = ec.tax;
      if (ec.items) p.items = ec.items;
    }
    if (evt.search_term) p.search_term = evt.search_term;
    if (evt.page_location) p.page_location = evt.page_location;
    if (evt.page_title) p.page_title = evt.page_title;
    // GA4 needs these two or the hit lands outside a session and realtime hides it
    if (evt.session_id) p.session_id = evt.session_id;
    p.engagement_time_msec = 1;
    return p;
  }

  function body(evt) {
    var out = {
      client_id: evt.client_id || 'dl-anon',
      events: [{ name: evt.event, params: params(evt) }]
    };
    if (evt.user_id) out.user_id = evt.user_id;
    return out;
  }

  /**
   * send(evt) -- fire one event. Returns true if a request was dispatched.
   * Never throws: a tracking failure must never break the storefront.
   */
  function send(evt) {
    var url = endpointUrl();
    if (!url) {
      DL.debug.drop('ga4mp-not-configured (needs ga4MeasurementId + ga4.apiSecret)', evt.event);
      return false;
    }
    var f = (typeof fetch !== 'undefined') ? fetch
      : (typeof window !== 'undefined' && window.fetch);
    if (!f) { DL.debug.drop('ga4mp-no-fetch', evt.event); return false; }
    try {
      f(url, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' }, // simple request: no CORS preflight
        body: JSON.stringify(body(evt)),
        keepalive: true
      })['catch'](function () { /* network errors are never fatal */ });
    } catch (e) {
      DL.debug.error('ga4mp send failed', e);
      return false;
    }
    return true;
  }

  return { send: send, _body: body, _endpointUrl: endpointUrl };
})();

/* ============================================================================
 * core/emit.js -- the ONLY dataLayer.push chokepoint
 *
 * Pipeline:  build -> validate -> consent gate -> dedup guard -> push
 * Nothing anywhere else in this codebase calls dataLayer.push for events.
 *
 * Guards (each one is a past client bug):
 *   - purchase without transaction_id or value<=0    -> blocked  (mgolda92)
 *   - purchase re-fire (thank-you refresh)           -> blocked  (spacegoods)
 *   - missing currency on value events               -> blocked  (value:null)
 *   - same logical event twice within 2s             -> blocked  (double emitters)
 * ========================================================================== */

DL.emit = (function () {
  /* -------------------------------------------------------- logical-key dedup */
  // key = event name + sorted item ids; duplicate = same key within a SLIDING
  // 2.5s window. (A fixed time-bucket had a boundary hole: two fires 1.9s
  // apart could land in different buckets and both pass -- caught live on
  // shophubdemo when cart-dom + AJAX intercept both handled one add.)
  var seen = {};
  var DEDUP_WINDOW_MS = 2500;

  function logicalKey(evt) {
    var ids = '';
    var items = DL.safe.get(evt, 'ecommerce.items');
    if (Array.isArray(items)) {
      ids = items.map(function (it) { return it.item_id + 'x' + it.quantity; }).sort().join(',');
    }
    return evt.event + '|' + ids;
  }

  function isDuplicate(evt) {
    // owner's call: report every action, suppress nothing unless asked to
    if (!DL.config.suppressDuplicates) return false;
    // purchase has its own persistent marker; meta events are exempt
    if (evt.event === 'purchase') return false;
    if (evt.event === 'consent_state' || evt.event === 'context_ready') return false;
    var key = logicalKey(evt);
    var now = Date.now();
    var last = seen[key];
    seen[key] = now;
    // keep the map small
    var keys = Object.keys(seen);
    if (keys.length > 200) delete seen[keys[0]];
    return last !== undefined && (now - last) < DEDUP_WINDOW_MS;
  }

  /* --------------------------------------------------- purchase fired-once */
  // Persistent (localStorage + cookie fallback), keyed by order id, 30d TTL.
  // In-memory flags reset on refresh -- that was exactly the spacegoods bug.
  var MARKER_PREFIX = '_dl_pur_';
  var MARKER_TTL_DAYS = 30;

  function purchaseAlreadyFired(orderId) {
    var key = MARKER_PREFIX + orderId;
    try {
      if (typeof localStorage !== 'undefined' && localStorage.getItem(key)) return true;
    } catch (e) { /* storage blocked -- fall through to cookie */ }
    if (typeof document !== 'undefined' && document.cookie &&
        document.cookie.indexOf(key + '=1') !== -1) return true;
    return false;
  }

  function markPurchaseFired(orderId) {
    var key = MARKER_PREFIX + orderId;
    try {
      if (typeof localStorage !== 'undefined') localStorage.setItem(key, String(Date.now()));
    } catch (e) { /* ignore */ }
    try {
      if (typeof document !== 'undefined') {
        document.cookie = key + '=1; max-age=' + (MARKER_TTL_DAYS * 86400) + '; path=/; SameSite=Lax';
      }
    } catch (e) { /* ignore */ }
  }

  /* --------------------------------------------------- server payload shape */
  // For transport 'endpoint' we speak the SERVER-SIDE GTM event data model, not
  // our browser dataLayer shape: `event_name` + ecommerce lifted to TOP LEVEL
  // (the GA4 server tag reads GA4-schema names, never a nested `ecommerce`
  // wrapper) and user_data under the documented sGTM field names. Doing this in
  // the pixel means ANY generic JSON client can ingest us -- no custom client
  // template required. The original event is kept under `x_datalayer` so nothing
  // is lost for custom server tags.
  function serverPayload(evt) {
    var ec = evt.ecommerce || {};
    var ud = evt.user_data || {};
    var raw = evt.user_data_raw || {};
    var out = {
      event_name: evt.event,
      event_id: evt.event_id,
      event_time: evt.event_time,
      event_source: evt.event_source,
      client_id: evt.client_id,
      session_id: evt.session_id,
      checkout_token: evt.checkout_token,
      page_location: evt.page_location,
      page_title: evt.page_title,
      page_referrer: evt.page_referrer,
      language: evt.language,
      currency: ec.currency,
      value: ec.value,
      transaction_id: ec.transaction_id,
      coupon: ec.coupon,
      shipping: ec.shipping,
      tax: ec.tax,
      items: ec.items,
      user_data: {
        email_address: raw.email,
        sha256_email_address: ud.sha256_email,
        phone_number: raw.phone,
        sha256_phone_number: ud.sha256_phone,
        address: {
          first_name: raw.first_name,
          sha256_first_name: ud.sha256_first_name,
          last_name: raw.last_name,
          sha256_last_name: ud.sha256_last_name,
          street: raw.street,
          sha256_street: ud.sha256_street,
          city: ud.city,
          region: ud.region,
          postal_code: ud.postal_code,
          country: ud.country
        }
      },
      x_datalayer: evt      // full canonical event for custom server tags
    };

    // Click ids: what an ad platform matches a server-side conversion back to
    // the click that caused it. Promoted to the top level as well, because that
    // is where each vendor mapping looks for them.
    var clicks = DL.identity && DL.identity.getClickIds && DL.identity.getClickIds();
    if (clicks) {
      out.click_ids = clicks;
      for (var ck in clicks) { if (out[ck] === undefined) out[ck] = clicks[ck]; }
    }

    // Consent travels with the event: a Conversions API call made without the
    // visitor's marketing consent is a compliance breach, and the server has no
    // other way to know. (Events are already gated here, so this is the state
    // at the moment of sending, not a promise about the future.)
    if (DL.consent && DL.consent.state) {
      out.consent = {
        analytics_storage: DL.consent.state.analytics ? 'granted' : 'denied',
        ad_storage: DL.consent.state.marketing ? 'granted' : 'denied',
        ad_user_data: DL.consent.state.marketing ? 'granted' : 'denied'
      };
    }

    // Which platforms the SERVER is responsible for, plus their ids. This
    // travels with every event so one sGTM/relay configuration can serve any
    // number of stores: the server fires what it is told to fire, and never
    // what the browser already fired.
    if (DL.destinations) {
      var names = DL.destinations.serverList();
      if (names.length) {
        var matrix = DL.destinations.all();
        out.send_to = names;
        out.destinations = {};
        for (var i = 0; i < names.length; i++) out.destinations[names[i]] = matrix[names[i]];
      }
    }

    // strip undefined so the JSON stays small and clean
    for (var k in out) { if (out[k] === undefined) delete out[k]; }
    return out;
  }

  /* ------------------------------------------------------------------- push */

  function targetDataLayer() {
    var name = DL.safe.get(DL, 'config.gtm.dataLayerName') || 'dataLayer';
    window[name] = window[name] || [];
    return window[name];
  }

  /**
   * push(evt) -- the chokepoint. evt is a fully-built flat event object.
   * Returns true if pushed, false if blocked (reason logged either way).
   */
  function push(evt) {
    if (!evt || !evt.event) return false;

    // per-event kill switch
    if (DL.config && DL.config.disabledEvents.indexOf(evt.event) !== -1) {
      DL.debug.drop('disabled-by-config', evt.event);
      return false;
    }

    // 1. validate against the contract
    var v = DL.schema.validate(evt);
    if (!v.ok) {
      DL.debug.drop('validation-failed', evt.event, v.errors);
      return false;
    }

    // 2. consent gate (theme side; pixel side is gated by Shopify itself)
    if (DL.consent && !DL.consent.allowed(evt.event)) {
      DL.consent.enqueue(evt);
      return false;
    }

    // 3. purchase-specific persistent dedup
    if (evt.event === 'purchase') {
      var orderId = DL.safe.get(evt, 'ecommerce.transaction_id');
      if (purchaseAlreadyFired(orderId)) {
        DL.debug.drop('dedup-blocked (purchase already fired)', 'purchase', orderId);
        return false;
      }
      markPurchaseFired(orderId);
    }

    // 4. logical-key dedup
    if (isDuplicate(evt)) {
      DL.debug.drop('dedup-blocked', evt.event);
      return false;
    }

    // 5a. transport: 'ga4mp' sends the GA4 Measurement Protocol straight from
    // here. This is the path for a merchant with NO GTM and NO server -- and it
    // is the only one that works inside a Shopify APP pixel (strict sandbox:
    // no external JS, but fetch is allowed).
    var tr = DL.config.transport;
    if (tr && tr.mode === 'ga4mp') {
      if (!DL.ga4mp || !DL.ga4mp.send(evt)) return false;
      DL.debug.trace(evt);
      return true;
    }

    // 5b. transport: 'endpoint' POSTs to an sGTM/relay URL (the only path in a
    // Shopify APP pixel's strict sandbox -- GTM cannot load there); default is
    // the classic dataLayer push for browser GTM.
    if (tr && tr.mode === 'endpoint' && tr.url) {
      var body = serverPayload(evt);
      try {
        var f = (typeof fetch !== 'undefined') ? fetch
          : (typeof window !== 'undefined' && window.fetch);
        if (f) {
          // text/plain on purpose: a "simple request" needs NO CORS preflight,
          // so the sGTM client only has to set Access-Control-Allow-Origin on
          // the response (it JSON.parses the body regardless of content type).
          f(tr.url, {
            method: 'POST',
            headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
            body: JSON.stringify(body),
            keepalive: true
          })['catch'](function () { /* network errors must never break the shop */ });
        } else {
          DL.debug.drop('transport-failed (no fetch)', evt.event);
          return false;
        }
      } catch (e) {
        DL.debug.error('endpoint transport failed', e);
        return false;
      }
      DL.debug.trace(evt);
      return true;
    }

    // clear ecommerce (GA4 recommendation: prevents object bleed), then push
    var dl = targetDataLayer();
    if (evt.ecommerce) dl.push({ ecommerce: null });
    dl.push(evt);

    // GTM-less stores: we also send the event to GA4 ourselves. No-op (and
    // loudly refused) in the default 'gtm' mode, so GTM + us can never both
    // send the same event.
    if (DL.config.mode === 'ga4' && DL.ga4direct) DL.ga4direct.send(evt);

    DL.debug.trace(evt);
    return true;
  }

  /* Test hooks */
  function _reset() { seen = {}; }

  return {
    push: push,
    purchaseAlreadyFired: purchaseAlreadyFired,
    markPurchaseFired: markPurchaseFired,
    _reset: _reset
  };
})();

/* ============================================================================
 * core/builder.js -- canonical event -> final flat dataLayer event
 *
 * One builder for BOTH halves (theme + pixel) so field names, types and
 * context handling can never drift between them.
 *
 * Canonical event (from adapters):
 * {
 *   name: 'add_to_cart',
 *   items: [canonicalItem...],         // see normalize.item
 *   currency: 'USD',
 *   value: { amount, unit },           // raw + declared unit
 *   coupon, transactionId, tax, shipping, subtotal,   // optional {amount,unit} for money
 *   listId, listName, searchTerm,
 *   customer: { id, type },            // type: 'New' | 'Existing'
 *   context: { url, referrer, title, path, search, hash, language, screenW, screenH, userAgent },
 *   identity: { clientId, sessionId, shopifyClientId },
 *   eventSource: 'theme' | 'custom_pixel',
 *   timestamp: ISO string (optional; defaults to now)
 * }
 * ========================================================================== */

DL.builder = (function () {
  var N = null; // set per call to DL.normalize (kept short)

  function moneyOf(m) {
    if (!m) return undefined;
    return N.round2(N.money(m.amount, m.unit));
  }

  /**
   * build(canon) -> flat event object ready for DL.emit.push, or null when
   * the event is structurally unusable (reason logged).
   */
  function build(canon) {
    N = DL.normalize;
    var cfg = DL.config;
    var put = N.put;

    var ts = canon.timestamp || new Date().toISOString();
    var tsSeconds = Math.floor(new Date(ts).getTime() / 1000);

    var evt = {
      event: canon.name,
      event_id: DL.identity.eventId(canon.name, canon.transactionId),
      event_time: ts,
      event_time_seconds: tsSeconds,
      event_source: canon.eventSource || 'theme'
    };

    /* ---- identity (sGTM-mandatory fields) */
    put(evt, 'client_id', N.str(DL.safe.get(canon, 'identity.clientId')));
    put(evt, 'session_id', N.str(DL.safe.get(canon, 'identity.sessionId')));
    put(evt, 'shopify_client_id', N.str(DL.safe.get(canon, 'identity.shopifyClientId')));

    /* ---- page context: ALWAYS from the current event, never cached (C8) */
    var c = canon.context || {};
    put(evt, 'page_location', N.str(c.url));
    put(evt, 'page_referrer', N.str(c.referrer));
    put(evt, 'page_title', N.str(c.title));
    put(evt, 'page_path', N.str(c.path));
    put(evt, 'search_param', N.str(c.search));
    put(evt, 'hash_param', N.str(c.hash));
    put(evt, 'language', N.str(c.language));
    put(evt, 'screen_width', N.int(c.screenW));
    put(evt, 'screen_height', N.int(c.screenH));
    put(evt, 'user_agent', N.str(c.userAgent));

    /* ---- customer */
    put(evt, 'customer_id', N.id(DL.safe.get(canon, 'customer.id')));
    var ctype = DL.safe.get(canon, 'customer.type');
    if (ctype === 'New' || ctype === 'Existing') evt.customer_type = ctype;

    /* ---- event-level id arrays (always string[], null-filtered) */
    if (Array.isArray(canon.items) && canon.items.length) {
      evt.product_variant_id = N.idArray(canon.items, 'variantId');
      evt.product_sku = N.idArray(canon.items, 'sku');
      var totalQty = 0;
      for (var i = 0; i < canon.items.length; i++) {
        totalQty += N.int(canon.items[i].quantity) || 1;
      }
      evt.itemTotalQuantity = totalQty;
    }

    /* ---- extra params */
    put(evt, 'search_term', N.str(canon.searchTerm));
    put(evt, 'payment_gateway', N.str(canon.paymentGateway));
    put(evt, 'checkout_token', N.str(canon.checkoutToken));
    put(evt, 'lead_type', N.str(canon.leadType));
    put(evt, 'form_id', N.str(canon.formId));
    put(evt, 'link_url', N.str(canon.linkUrl));
    put(evt, 'promotion_id', N.str(canon.promotionId));
    put(evt, 'promotion_name', N.str(canon.promotionName));
    put(evt, 'customer_country', N.str(canon.customerCountry));
    put(evt, 'customer_state', N.str(canon.customerState));
    // multi-currency: shop (base) currency alongside ecommerce.currency
    // (presentment). Entries/adapters fill config.shopCurrency; core stays
    // platform-agnostic and just emits it.
    put(evt, 'shop_currency', N.str(canon.shopCurrency || DL.safe.get(DL, 'config.shopCurrency')));

    /* ---- ecommerce */
    var needsEcom = canon.items || canon.value || canon.transactionId || canon.currency;
    if (needsEcom) {
      var ec = {};
      put(ec, 'currency', N.str(canon.currency));

      // value: config decides whether tax/shipping ride inside -- ONE rule,
      // identical for every event, documented to the client.
      var value = moneyOf(canon.value);
      var tax = moneyOf(canon.tax);
      var shipping = moneyOf(canon.shipping);
      if (value !== undefined && canon.name === 'purchase') {
        if (!cfg.purchaseValueIncludes.tax && tax !== undefined) value = N.round2(value - tax);
        if (!cfg.purchaseValueIncludes.shipping && shipping !== undefined) value = N.round2(value - shipping);
      }
      put(ec, 'value', value);
      put(ec, 'tax', tax);                    // always separate params too
      put(ec, 'shipping', shipping);
      put(ec, 'subtotal', moneyOf(canon.subtotal));
      put(ec, 'coupon', N.str(canon.coupon));
      put(ec, 'transaction_id', N.id(canon.transactionId));
      put(ec, 'item_list_id', N.str(canon.listId));
      put(ec, 'item_list_name', N.str(canon.listName));

      if (canon.items) {
        ec.items = N.items(canon.items, cfg);
      }
      evt.ecommerce = ec;
    }

    /* ---- browser-side platform blocks (default OFF with sGTM) */
    if (cfg.browserPlatformBlocks && evt.ecommerce) {
      DL.platforms.attach(evt, canon);
    }

    return evt;
  }

  /**
   * Full pipeline sugar: build + attach hashed user_data (async) + push.
   * Returns a Promise<boolean>.
   */
  function emit(canon) {
    var evt = build(canon);
    if (!evt) return Promise.resolve(false);
    if (canon.userData) {
      // raw (normalized, unhashed) for browser Google User-Provided Data + Meta
      // Advanced Matching -- vendor SDKs hash in-browser. Attached synchronously.
      var rawUd = DL.identity.buildUserDataRaw(canon.userData);
      if (rawUd) evt.user_data_raw = rawUd;
      // sha256 (for sGTM / server-side) -- async.
      return DL.identity.buildUserData(canon.userData).then(function (ud) {
        if (ud) evt.user_data = ud;
        return DL.emit.push(evt);
      });
    }
    return Promise.resolve(DL.emit.push(evt));
  }

  return { build: build, emit: emit };
})();

/* ============================================================================
 * core/platforms.js -- browser-side platform parameter blocks
 *
 * fb_browser_side / pt_browser_side / snap_browser_side / tt_browser_side /
 * li_browser_side / gads_browser_side
 *
 * DEFAULT OFF when serverSide.enabled -- the sGTM templates build these
 * payloads; duplicating them in the browser is three more places to break.
 * All blocks are built from the ALREADY-normalized ecommerce object, so a
 * platform block can never disagree with the GA4 payload (reference-script
 * bug class: 6 hand-copied mapping blocks drifting apart).
 *
 * Type rules hold here too: arrays are always arrays, strings always strings
 * (reference bug: `brands` was sometimes a string, sometimes an array).
 * ========================================================================== */

DL.platforms = (function () {
  function enabled(name) {
    // The destinations matrix wins when the merchant configured one: a block
    // is built ONLY if that platform is fed from the browser. Server-only
    // platforms get their payload built server-side instead.
    var matrix = DL.destinations && DL.destinations.all();
    if (matrix && Object.keys(matrix).length) return DL.destinations.sendsClient(name);

    var list = DL.safe.get(DL, 'config.platforms') || [];
    for (var i = 0; i < list.length; i++) {
      if (String(list[i]).toLowerCase().indexOf(name) !== -1) return true;
    }
    return false;
  }

  function itemsOf(evt) { return DL.safe.get(evt, 'ecommerce.items') || []; }

  function contentIds(evt) {
    return itemsOf(evt).map(function (it) { return it.item_id; });
  }

  function contents(evt) {
    return itemsOf(evt).map(function (it) {
      return { id: it.item_id, quantity: it.quantity, item_price: it.price };
    });
  }

  function numItems(evt) {
    var n = 0;
    itemsOf(evt).forEach(function (it) { n += it.quantity || 1; });
    return n;
  }

  function attach(evt) {
    var ec = evt.ecommerce;
    if (!ec) return evt;
    var put = DL.normalize.put;

    if (enabled('facebook')) {
      var fb = {
        content_type: 'product',
        content_ids: contentIds(evt),
        contents: contents(evt)
      };
      put(fb, 'currency', ec.currency);
      put(fb, 'value', ec.value);
      put(fb, 'num_items', numItems(evt) || undefined);
      evt.fb_browser_side = fb;
    }

    if (enabled('pinterest')) {
      var pt = {
        line_items: itemsOf(evt).map(function (it) {
          var li = { product_id: it.item_id, product_quantity: it.quantity };
          put(li, 'product_name', it.item_name);
          put(li, 'product_brand', it.item_brand);
          put(li, 'product_category', it.item_category);
          put(li, 'product_price', it.price);
          return li;
        })
      };
      put(pt, 'currency', ec.currency);
      put(pt, 'value', ec.value);
      put(pt, 'order_quantity', numItems(evt) || undefined);
      put(pt, 'order_id', ec.transaction_id);
      put(pt, 'promo_code', ec.coupon);
      evt.pt_browser_side = pt;
    }

    if (enabled('snapchat')) {
      var snap = {
        item_ids: contentIds(evt),
        // ALWAYS an array -- never a ""/array chameleon
        brands: itemsOf(evt).map(function (it) { return it.item_brand; })
          .filter(function (b) { return b !== undefined; })
      };
      put(snap, 'currency', ec.currency);
      put(snap, 'price', ec.value);
      put(snap, 'number_items', numItems(evt) || undefined);
      put(snap, 'transaction_id', ec.transaction_id);
      evt.snap_browser_side = snap;
    }

    if (enabled('tiktok')) {
      var tt = {
        contents: itemsOf(evt).map(function (it) {
          var c = { content_id: it.item_id, content_type: 'product', quantity: it.quantity };
          put(c, 'content_name', it.item_name);
          put(c, 'price', it.price);
          return c;
        })
      };
      put(tt, 'currency', ec.currency);
      put(tt, 'value', ec.value);
      evt.tt_browser_side = tt;
    }

    if (enabled('linkedin')) {
      var li2 = {};
      put(li2, 'currency', ec.currency);
      put(li2, 'conversion_value', ec.value);
      put(li2, 'order_id', ec.transaction_id);
      evt.li_browser_side = li2;
    }

    if (enabled('google_ads')) {
      var ga = {
        items: itemsOf(evt).map(function (it) {
          var g = { id: it.item_id, quantity: it.quantity };
          put(g, 'price', it.price);
          return g;
        })
      };
      put(ga, 'currency', ec.currency);
      put(ga, 'value', ec.value);
      put(ga, 'transaction_id', ec.transaction_id);
      evt.gads_browser_side = ga;
    }

    return evt;
  }

  return { attach: attach, enabled: enabled };
})();

/* ============================================================================
 * adapters/shopify/cart-ajax-adapter.js -- Cart AJAX API responses -> canonical
 *
 * moneyUnit: 'cents'  (verified: "price": 12900 means $129.00)
 * ids are INTEGERS here (verified: product_id: 5680114172069) -- coerced to
 * string in normalize, never here (single coercion point).
 *
 * Currency does NOT appear on /cart/add.js line items -- it comes from
 * /cart.js (cart.currency) or window.Shopify.currency.active. Never
 * hardcoded.
 * ========================================================================== */

DL.adapters = DL.adapters || {};

DL.adapters.shopifyCartAjax = (function () {
  var UNIT = 'cents';
  var get = function (o, p, f) { return DL.safe.get(o, p, f); };

  function m(amount) {
    return amount == null ? undefined : { amount: amount, unit: UNIT };
  }

  /** Storefront currency: Shopify global; caller may override from /cart.js. */
  function currency() {
    return get(window, 'Shopify.currency.active');
  }

  /** One Cart AJAX line item -> canonical item. */
  function item(li, index) {
    return {
      productId: get(li, 'product_id'),
      variantId: get(li, 'variant_id') != null ? get(li, 'variant_id') : get(li, 'id'),
      sku: get(li, 'sku'),                       // verified: may be null
      name: get(li, 'product_title'),
      brand: get(li, 'vendor'),
      category: get(li, 'product_type'),
      variantTitle: get(li, 'variant_title'),
      price: m(get(li, 'price')),
      quantity: get(li, 'quantity'),
      index: index
    };
  }

  /**
   * /cart/add.js response -> canonical add_to_cart.
   * Response is either a single line item or { items: [...] }.
   */
  function fromAddResponse(json, curr) {
    var lines = Array.isArray(get(json, 'items')) ? json.items : [json];
    var items = [];
    var total = 0;
    for (var i = 0; i < lines.length; i++) {
      if (!lines[i]) continue;
      items.push(item(lines[i], i));
      var p = get(lines[i], 'price');
      var q = get(lines[i], 'quantity') || 1;
      if (typeof p === 'number') total += p * q;
    }
    if (!items.length) return null;
    return {
      name: 'add_to_cart',
      eventSource: 'theme',
      currency: curr || currency(),
      value: m(total),
      items: items
    };
  }

  /**
   * /cart/change.js | /cart/update.js: compare previous and new cart state to
   * decide add vs remove per line. prev/next: { items: [...] } cart JSON.
   * Returns an ARRAY of canonical events (may be empty).
   */
  function diffCarts(prev, next, curr) {
    var events = [];
    var prevMap = {};
    var i, li, key;

    var prevItems = get(prev, 'items') || [];
    for (i = 0; i < prevItems.length; i++) {
      key = String(get(prevItems[i], 'variant_id') || get(prevItems[i], 'id'));
      prevMap[key] = prevItems[i];
    }

    var nextItems = get(next, 'items') || [];
    var seen = {};
    for (i = 0; i < nextItems.length; i++) {
      li = nextItems[i];
      key = String(get(li, 'variant_id') || get(li, 'id'));
      seen[key] = true;
      var prevQty = prevMap[key] ? (get(prevMap[key], 'quantity') || 0) : 0;
      var nextQty = get(li, 'quantity') || 0;
      var delta = nextQty - prevQty;
      if (delta === 0) continue;
      var canonItem = item(li, i);
      canonItem.quantity = Math.abs(delta);
      var unitPrice = get(li, 'price');
      events.push({
        name: delta > 0 ? 'add_to_cart' : 'remove_from_cart',
        eventSource: 'theme',
        currency: curr || currency(),
        value: typeof unitPrice === 'number' ? m(unitPrice * Math.abs(delta)) : undefined,
        items: [canonItem]
      });
    }

    // lines that disappeared entirely
    for (key in prevMap) {
      if (seen[key]) continue;
      li = prevMap[key];
      var gone = item(li, 0);
      var gonePrice = get(li, 'price');
      events.push({
        name: 'remove_from_cart',
        eventSource: 'theme',
        currency: curr || currency(),
        value: typeof gonePrice === 'number' ? m(gonePrice * (get(li, 'quantity') || 1)) : undefined,
        items: [gone]
      });
    }

    return events;
  }

  /** Full /cart.js JSON -> canonical view_cart. */
  function fromCart(json, curr) {
    var lines = get(json, 'items') || [];
    var items = [];
    for (var i = 0; i < lines.length; i++) items.push(item(lines[i], i));
    return {
      name: 'view_cart',
      eventSource: 'theme',
      currency: curr || get(json, 'currency') || currency(),
      value: m(get(json, 'total_price')),
      items: items
    };
  }

  return {
    moneyUnit: UNIT,
    currency: currency,
    item: item,
    fromAddResponse: fromAddResponse,
    diffCarts: diffCarts,
    fromCart: fromCart
  };
})();

/* ============================================================================
 * adapters/shopify/liquid-adapter.js -- Liquid-injected JSON -> canonical
 *
 * moneyUnit: 'cents'  (Liquid product.price / line_item.price are integer cents)
 *
 * The theme snippet (snippets/dl-data.liquid) renders page data as
 * <script type="application/json" data-dl="..."> blocks; this adapter parses
 * them. Reading structured JSON -- never scraping the DOM (rendered markup
 * differs per theme; the JSON contract doesn't).
 * ========================================================================== */

DL.adapters = DL.adapters || {};

DL.adapters.shopifyLiquid = (function () {
  var UNIT = 'cents';
  var get = function (o, p, f) { return DL.safe.get(o, p, f); };

  function m(amount) {
    return amount == null ? undefined : { amount: amount, unit: UNIT };
  }

  /** Read + parse a <script type="application/json" data-dl="<kind>"> block. */
  function readBlock(kind) {
    if (typeof document === 'undefined') return undefined;
    var el = document.querySelector('script[type="application/json"][data-dl="' + kind + '"]');
    if (!el) return undefined;
    try {
      return JSON.parse(el.textContent);
    } catch (err) {
      DL.debug.error('dl-data block "' + kind + '" is not valid JSON', err);
      return undefined;
    }
  }

  /** Snippet's product JSON (product page) -> canonical view_item. */
  function fromProduct(data, curr) {
    if (!data) return null;
    var v = get(data, 'selected_variant') || get(data, 'variants.0') || {};
    return {
      name: 'view_item',
      eventSource: 'theme',
      currency: curr || get(data, 'currency') || DL.adapters.shopifyCartAjax.currency(),
      value: m(get(v, 'price') != null ? get(v, 'price') : get(data, 'price')),
      items: [{
        productId: get(data, 'id'),
        variantId: get(v, 'id'),
        sku: get(v, 'sku'),
        name: get(data, 'title'),
        brand: get(data, 'vendor'),
        category: get(data, 'type'),
        variantTitle: get(v, 'title'),
        price: m(get(v, 'price') != null ? get(v, 'price') : get(data, 'price')),
        quantity: 1
      }]
    };
  }

  /** Snippet's collection JSON -> canonical view_item_list (rendered order + index). */
  function fromCollection(data, curr) {
    if (!data || !Array.isArray(data.products) || !data.products.length) return null;
    var items = [];
    for (var i = 0; i < data.products.length; i++) {
      var p = data.products[i];
      items.push({
        productId: get(p, 'id'),
        variantId: get(p, 'variant_id'),
        sku: get(p, 'sku'),
        name: get(p, 'title'),
        brand: get(p, 'vendor'),
        category: get(p, 'type'),
        price: m(get(p, 'price')),
        quantity: 1,
        index: i,                                   // rendered position -- pixel can't give this
        listId: get(data, 'id'),
        listName: get(data, 'title')
      });
    }
    return {
      name: 'view_item_list',
      eventSource: 'theme',
      currency: curr || get(data, 'currency') || DL.adapters.shopifyCartAjax.currency(),
      listId: DL.normalize.id(get(data, 'id')),
      listName: get(data, 'title'),
      items: items
    };
  }

  /** Snippet's search JSON -> canonical search + view_search_results pair. */
  function fromSearch(data) {
    if (!data) return null;
    return {
      name: 'search',
      eventSource: 'theme',
      searchTerm: get(data, 'terms')
    };
  }

  /** Logged-in customer JSON -> identity enrichment (id only; PII -> hashed). */
  function customer() {
    return readBlock('customer');
  }

  return {
    moneyUnit: UNIT,
    readBlock: readBlock,
    fromProduct: fromProduct,
    fromCollection: fromCollection,
    fromSearch: fromSearch,
    customer: customer
  };
})();

/* ============================================================================
 * theme/selectors.js -- theme DOM selector presets
 *
 * select_item / promotions / leads need to recognize theme markup, and every
 * theme's markup differs. So selectors are CONFIG, never hardcoded in logic.
 * Two presets ship (Dawn-family + generic); per-store overrides go in
 * config.selectors. In debug mode a selector that matches 0 elements warns
 * loudly instead of silently losing events.
 * ========================================================================== */

DL.selectors = (function () {
  var PRESETS = {
    // Dawn and Dawn-derived themes (majority of new stores)
    dawn: {
      productCard: '.card-wrapper, .card--card, product-card',
      productCardLink: 'a[href*="/products/"]',
      collectionGrid: '#product-grid, .collection .grid, ul.grid--uniform',
      promoSlot: '[data-promo], .banner, .slideshow__slide',
      promoLink: 'a',
      signupForm: 'form[action*="/account"], form#create_customer',
      loginForm: 'form[action*="/account/login"]'
    },
    // Conservative fallback for unknown themes
    generic: {
      productCard: '[class*="product-card"], [class*="product-item"], [class*="card-wrapper"], li.grid__item',
      productCardLink: 'a[href*="/products/"]',
      collectionGrid: '[id*="product-grid"], [class*="collection-grid"], [class*="product-grid"]',
      promoSlot: '[data-promo], [class*="banner"], [class*="promo"]',
      promoLink: 'a',
      signupForm: 'form#create_customer, form[action*="/account"][method="post"]',
      loginForm: 'form[action*="/account/login"]'
    }
  };

  function resolve() {
    var cfg = DL.config;
    var preset = PRESETS[cfg.selectorPreset] || PRESETS.generic;
    var out = {};
    for (var k in preset) out[k] = preset[k];
    for (var o in cfg.selectors) out[o] = cfg.selectors[o]; // per-store overrides win
    return out;
  }

  /** querySelectorAll + loud warning when a selector matches nothing. */
  function all(key) {
    var sel = resolve()[key];
    if (!sel || typeof document === 'undefined') return [];
    var nodes;
    try {
      nodes = document.querySelectorAll(sel);
    } catch (err) {
      DL.debug.error('selector "' + key + '" is invalid CSS: ' + sel, err);
      return [];
    }
    if (nodes.length === 0 && DL.debug.enabled()) {
      DL.debug.warn('selector "' + key + '" matched 0 elements (' + sel + ') -- tune config.selectors for this theme');
    }
    return [].slice.call(nodes);
  }

  return { resolve: resolve, all: all, PRESETS: PRESETS };
})();

/* ============================================================================
 * theme/cart-intercept.js --  fetch + XHR patch for the Cart AJAX API
 *
 * Every Shopify theme drives its cart through the same endpoints:
 *   POST /cart/add.js     -> add_to_cart
 *   POST /cart/change.js  -> add_to_cart | remove_from_cart (quantity diff)
 *   POST /cart/update.js  -> diff against previous cart state
 * So intercepting them is theme-agnostic: Dawn, Debut, custom, quick-add,
 * cart drawers, most upsell apps -- all captured without a single DOM selector.
 *
 * HARD REQUIREMENT -- the patch must be harmless:
 *   - the original function is ALWAYS called, first, unchanged
 *   - our handling runs on a CLONE of the response, inside try/catch
 *   - we never touch the promise chain the theme sees
 * A data-layer bug must never break a merchant's cart. Ever.
 *
 * Known gaps (documented, by design): subscription apps (ReCharge-class) and
 * some upsell apps use their own APIs or the Storefront API -- those adds are
 * not visible here. Headless/Hydrogen stores don't run theme JS at all.
 * ========================================================================== */

DL.cartIntercept = (function () {
  var lastCartState = null;   // previous /cart.js JSON for diffing change/update
  var handledMark = 0;        // timestamp of last AJAX-handled cart event (for cart-dom skip)
  var removalsOnly = false;   // pixel mode: the pixel owns adds, we only add removals

  function isCartUrl(url, endpoint) {
    if (typeof url !== 'string') return false;
    // matches /cart/add.js and /{locale}/cart/add.js, tolerates querystrings
    return url.indexOf('/cart/' + endpoint) !== -1;
  }

  function refreshCartState() {
    // background fetch of /cart.js to keep the diff baseline fresh; uses the
    // ORIGINAL fetch so we don't re-enter our own patch
    try {
      originalFetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (json) { lastCartState = json; })
        .catch(function () { /* keep old baseline */ });
    } catch (e) { /* ignore */ }
  }

  function handleResponse(url, json) {
    try {
      var Adapter = DL.adapters.shopifyCartAjax;
      var events = [];

      if (isCartUrl(url, 'add')) {
        var canon = Adapter.fromAddResponse(json);
        if (canon) events.push(canon);
        refreshCartState();
      } else if (isCartUrl(url, 'change') || isCartUrl(url, 'update')) {
        // change/update responses return the full cart -- diff against baseline
        if (lastCartState) {
          events = Adapter.diffCarts(lastCartState, json);
        }
        lastCartState = json;
      }

      if (removalsOnly) {
        var only = [];
        for (var r = 0; r < events.length; r++) {
          if (events[r] && events[r].name === 'remove_from_cart') only.push(events[r]);
        }
        events = only;
      }

      for (var i = 0; i < events.length; i++) {
        events[i].context = DL.theme.pageContext();
        events[i].identity = DL.theme.identity();
        if (events[i].name === 'remove_from_cart') DL.cartBridge.emit(events[i]);
        else DL.builder.emit(events[i]);
      }
      if (events.length) handledMark = Date.now();
    } catch (err) {
      DL.debug.error('cart-intercept handling failed (cart itself unaffected)', err);
    }
  }

  /* ------------------------------------------------------------- fetch patch */
  var originalFetch = null;

  function patchFetch() {
    if (typeof window.fetch !== 'function') return;
    originalFetch = window.fetch.bind(window);
    window.fetch = function (input, init) {
      // original call FIRST, untouched -- its promise is what the theme gets
      var promise = originalFetch(input, init);
      try {
        var url = typeof input === 'string' ? input
          : (input && typeof input.url === 'string' ? input.url : '');
        if (isCartUrl(url, 'add') || isCartUrl(url, 'change') || isCartUrl(url, 'update')) {
          promise.then(function (response) {
            if (!response || !response.ok) return;
            // work on a CLONE so the theme's own response.json() still works
            response.clone().json()
              .then(function (json) { handleResponse(url, json); })
              .catch(function () { /* non-JSON response -- ignore */ });
          }).catch(function () { /* network error is the theme's concern */ });
        }
      } catch (err) {
        DL.debug.error('cart-intercept fetch tap failed (cart unaffected)', err);
      }
      return promise;
    };
  }

  /* --------------------------------------------------------------- XHR patch */
  function patchXhr() {
    if (typeof XMLHttpRequest === 'undefined') return;
    var origOpen = XMLHttpRequest.prototype.open;
    var origSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url) {
      try { this.__dl_url = url; } catch (e) { /* ignore */ }
      return origOpen.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function () {
      try {
        var xhr = this;
        var url = xhr.__dl_url || '';
        if (isCartUrl(url, 'add') || isCartUrl(url, 'change') || isCartUrl(url, 'update')) {
          xhr.addEventListener('load', function () {
            try {
              if (xhr.status < 200 || xhr.status >= 300) return;
              var json = JSON.parse(xhr.responseText);
              handleResponse(url, json);
            } catch (err) { /* non-JSON -- ignore */ }
          });
        }
      } catch (err) {
        DL.debug.error('cart-intercept xhr tap failed (cart unaffected)', err);
      }
      return origSend.apply(this, arguments);
    };
  }

  /* ------------------------------------------------------------------- init */
  /**
   * init(opts)
   *   opts.removalsOnly -- emit ONLY remove_from_cart from the intercepted calls.
   *   Used in 'pixel' ownership mode: the pixel already owns add_to_cart via
   *   Shopify's standard events, but Shopify emits NOTHING we can rely on for
   *   removals across themes, so the theme closes that gap for every store.
   */
  function init(opts) {
    removalsOnly = !!(opts && opts.removalsOnly);
    patchFetch();
    patchXhr();
    // baseline cart state for change/update diffing
    refreshCartState();
    DL.debug.log('cart-intercept active (/cart/add.js, change.js, update.js)' +
      (removalsOnly ? ' -- removals only' : ''));
  }

  /** cart-dom uses this: was an AJAX cart event handled in the last 2s? */
  function recentlyHandled() {
    return Date.now() - handledMark < 2000;
  }

  return { init: init, recentlyHandled: recentlyHandled, _handleResponse: handleResponse };
})();

/* ============================================================================
 * theme/cart-dom.js -- DOM fallback for non-AJAX themes
 *
 * Old/simple themes submit form[action*="/cart/add"] with a full page reload --
 * no fetch/XHR to intercept. This listener covers them. On AJAX themes the
 * intercept fires first and this path SKIPS (recentlyHandled + the emit-layer
 * logical-key guard back it up), so it can never double-fire.
 * ========================================================================== */

DL.cartDom = (function () {
  function onSubmit(e) {
    try {
      var form = e.target;
      if (!form || !form.action || String(form.action).indexOf('/cart/add') === -1) return;
      if (DL.cartIntercept.recentlyHandled()) return; // AJAX path already got it

      // variant id from the form; product data from the Liquid block
      var idInput = form.querySelector('[name="id"]');
      var qtyInput = form.querySelector('[name="quantity"]');
      var variantId = idInput ? idInput.value : undefined;
      var qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

      var productData = DL.adapters.shopifyLiquid.readBlock('product');
      if (!productData || !variantId) {
        DL.debug.drop('handler-error', 'add_to_cart', 'cart-dom: no product block or variant id');
        return;
      }

      // find the matching variant in the Liquid JSON
      var variants = productData.variants || [];
      var v = null;
      for (var i = 0; i < variants.length; i++) {
        if (String(variants[i].id) === String(variantId)) { v = variants[i]; break; }
      }

      var price = v ? v.price : productData.price; // cents (Liquid)
      var canon = {
        name: 'add_to_cart',
        eventSource: 'theme',
        currency: productData.currency || DL.adapters.shopifyCartAjax.currency(),
        value: typeof price === 'number' ? { amount: price * qty, unit: 'cents' } : undefined,
        items: [{
          productId: productData.id,
          variantId: variantId,
          sku: v ? v.sku : undefined,
          name: productData.title,
          brand: productData.vendor,
          category: productData.type,
          variantTitle: v ? v.title : undefined,
          price: typeof price === 'number' ? { amount: price, unit: 'cents' } : undefined,
          quantity: qty
        }],
        context: DL.theme.pageContext(),
        identity: DL.theme.identity()
      };
      // NOTE: page is about to unload; dataLayer.push is synchronous so the
      // GTM tag gets its chance. transport beacon on the GA4 tag handles the rest.
      DL.builder.emit(canon);
    } catch (err) {
      DL.debug.error('cart-dom handler failed (form submit unaffected)', err);
    }
  }

  function init() {
    if (typeof document === 'undefined') return;
    document.addEventListener('submit', onSubmit, true); // capture: before theme handlers can stopPropagation
    DL.debug.log('cart-dom fallback active');
  }

  return { init: init };
})();

/* ============================================================================
 * theme/cart-bridge.js -- hand a theme-detected cart removal to the PIXEL
 *
 * Shopify never fires product_removed_from_cart for full-page carts, and misses
 * it on plenty of AJAX ones, so the theme is the only place that can SEE a
 * removal. But every other funnel event lives in the pixel sandbox, and a
 * merchant debugging with the DataLayer Checker expects remove_from_cart to sit
 * right there next to them -- not in a second, top-frame dataLayer.
 *
 * Shopify's own bridge for exactly this is the custom-event API:
 *   theme:  Shopify.analytics.publish('<name>', payload)
 *   pixel:  analytics.subscribe('<name>', handler)
 * So the theme DETECTS and publishes; the pixel EMITS. One event, one owner,
 * and it lands in the sandbox with event_source 'custom_pixel' like the rest.
 *
 * If the storefront has no publish API (older themes, headless), we fall back
 * to emitting from the theme -- the event is never lost, it just travels in the
 * top-frame dataLayer instead.
 * ========================================================================== */

DL.cartBridge = (function () {
  function publisher() {
    if (typeof window === 'undefined') return null;
    var a = window.Shopify && window.Shopify.analytics;
    return (a && typeof a.publish === 'function') ? a : null;
  }

  /** emit(canon) -- publish to the pixel when possible, else emit locally. */
  function emit(canon) {
    if (!canon) return false;
    if (DL.config.removalOwner === 'pixel') {
      var api = publisher();
      if (api) {
        try {
          api.publish(DL.config.removalBridgeEvent, {
            name: canon.name,
            currency: canon.currency,
            value: canon.value,
            items: canon.items
          });
          DL.debug.log('removal handed to the pixel sandbox', canon.name);
          return true;
        } catch (e) {
          DL.debug.error('Shopify.analytics.publish failed -- emitting from the theme', e);
        }
      } else {
        DL.debug.log('no Shopify.analytics.publish -- emitting removal from the theme');
      }
    }
    return DL.builder.emit(canon);
  }

  return { emit: emit, _publisher: publisher };
})();

/* ============================================================================
 * theme/cart-page.js -- remove_from_cart for FULL-PAGE cart themes
 *
 * Some themes drive the cart page with full-page navigation, not AJAX:
 *   - "Remove" is  <a class="cart__remove" data-id="VARIANT"
 *        href="/cart/change?line=N&quantity=0">   (a plain link -> page reload)
 *   - quantity is an <input name="updates[]"> submitted by a full-page
 *        form[action="/cart"]  ("Update cart")
 *
 * Neither path triggers Shopify's product_removed_from_cart standard event, and
 * there is no /cart/change.js fetch for cart-intercept to tap. So on these
 * themes remove_from_cart is simply lost -- the pixel never sees it, the AJAX
 * intercept never sees it, and cart-dom only covers /cart/add. This module
 * fills exactly that gap.
 *
 * How: snapshot /cart.js on cart-page load, then on a remove-link click or an
 * "update cart" submit, work out which lines shrink and emit remove_from_cart
 * for them -- synchronously, before the page unloads (dataLayer.push is sync, so
 * the GA4 tag gets its chance; its beacon transport does the rest).
 *
 * Safe by design: runs in BOTH ownership modes (Shopify never fires the event
 * for these themes, so there is no double), but SKIPS when the AJAX intercept
 * just handled a cart change (recentlyHandled), skips the /cart/add form
 * (cart-dom owns that), and the emit-layer 2.5s logical-key guard is the final
 * backstop. It only ever reads the cart and pushes an event -- it never touches
 * the theme's own remove/update behaviour.
 * ========================================================================== */

DL.cartPage = (function () {
  var baseline = null;   // /cart.js JSON snapshot taken at page load

  function snapshot() {
    try {
      var f = (typeof fetch !== 'undefined') ? fetch
        : (typeof window !== 'undefined' && window.fetch);
      if (!f) return;
      // /cart.js is NOT a cart-mutation endpoint, so cart-intercept's patch
      // ignores it -- safe to call the (possibly patched) fetch.
      f('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (j) { baseline = j; })
        .catch(function () { /* keep whatever baseline we have */ });
    } catch (e) { /* ignore */ }
  }

  function baseItems() { return (baseline && baseline.items) || []; }

  function ajaxJustHandled() {
    return !!(DL.cartIntercept && DL.cartIntercept.recentlyHandled &&
              DL.cartIntercept.recentlyHandled());
  }

  function emitRemove(line, qty) {
    if (!line) return;
    var q = qty || DL.safe.get(line, 'quantity') || 1;
    var Adapter = DL.adapters.shopifyCartAjax;
    var it = Adapter.item(line, 0);   // Cart AJAX line shape -> canonical item
    it.quantity = q;
    var price = DL.safe.get(line, 'price');   // cents (Cart AJAX)
    DL.cartBridge.emit({
      name: 'remove_from_cart',
      eventSource: 'theme',
      currency: (baseline && baseline.currency) || Adapter.currency(),
      value: typeof price === 'number' ? { amount: price * q, unit: 'cents' } : undefined,
      items: [it],
      context: DL.theme.pageContext(),
      identity: DL.theme.identity()
    });
  }

  /* -- "Remove" link: <a href="/cart/change?line=N&quantity=0" data-id="VID"> */
  function onClick(e) {
    try {
      if (ajaxJustHandled()) return;
      var t = e.target;
      var sel = 'a[href*="/cart/change"], a[href*="/change?"], .cart__remove, [data-cart-remove]';
      var a = (t && t.closest) ? t.closest(sel) : null;
      if (!a) return;

      var items = baseItems();
      var line = null;
      var href = a.getAttribute('href') || '';

      // Prefer the explicit Shopify line index (1-based) if present.
      var mLine = href.match(/[?&]line=(\d+)/);
      if (mLine) line = items[parseInt(mLine[1], 10) - 1] || null;

      // Otherwise match by variant id (data-id, or ?id= in the href).
      if (!line) {
        var vid = a.getAttribute('data-id') || (href.match(/[?&]id=(\d+)/) || [])[1];
        if (vid) {
          for (var i = 0; i < items.length; i++) {
            var k = DL.safe.get(items[i], 'variant_id');
            if (k == null) k = DL.safe.get(items[i], 'id');
            if (String(k) === String(vid)) { line = items[i]; break; }
          }
        }
      }
      // A remove link drops the whole line (quantity=0).
      emitRemove(line);
    } catch (err) {
      DL.debug.error('cart-page click handler failed (removal unaffected)', err);
    }
  }

  /* -- "Update cart": form[action*="/cart"] with updates[] (line order) -> diff */
  function onSubmit(e) {
    try {
      var form = e.target;
      var action = (form && form.action) ? String(form.action) : '';
      if (action.indexOf('/cart') === -1) return;
      if (action.indexOf('/cart/add') !== -1) return;   // add form is cart-dom's
      if (ajaxJustHandled()) return;

      var updates = form.querySelectorAll('[name="updates[]"]');
      if (!updates.length) return;
      var items = baseItems();
      for (var i = 0; i < updates.length; i++) {
        var line = items[i];               // updates[] are in cart line order
        if (!line) continue;
        var newQty = parseInt(updates[i].value, 10);
        if (isNaN(newQty)) continue;
        var delta = (DL.safe.get(line, 'quantity') || 0) - newQty;
        if (delta > 0) emitRemove(line, delta);
      }
    } catch (err) {
      DL.debug.error('cart-page submit handler failed (update unaffected)', err);
    }
  }

  function init() {
    if (typeof document === 'undefined') return;
    // Only the cart page carries these controls -- cheap early-out everywhere else.
    var sel = '[name="updates[]"], a[href*="/cart/change"], .cart__remove, [data-cart-remove]';
    if (!document.querySelector(sel)) return;
    snapshot();
    document.addEventListener('click', onClick, true);
    document.addEventListener('submit', onSubmit, true);
    DL.debug.log('cart-page removal handler active (full-page cart)');
  }

  return { init: init, _emitRemove: emitRemove, _onClick: onClick, _onSubmit: onSubmit };
})();

/* ============================================================================
 * theme/pages.js -- page-load events from Liquid-injected JSON
 *
 * page_view (every page)  view_item (product)  view_item_list (collection)
 * view_cart (cart page)  search + view_search_results (search page)
 *
 * Data source is the snippets/dl-data.liquid JSON blocks -- structured data
 * from Liquid, never scraped markup. Which event fires is decided by which
 * block the snippet rendered (the snippet knows the template).
 * ========================================================================== */

DL.pages = (function () {
  function firePageView() {
    DL.builder.emit({
      name: 'page_view',
      eventSource: 'theme',
      context: DL.theme.pageContext(),
      identity: DL.theme.identity()
    });
  }

  function fireProduct() {
    var data = DL.adapters.shopifyLiquid.readBlock('product');
    if (!data) return;
    var canon = DL.adapters.shopifyLiquid.fromProduct(data);
    if (!canon) return;
    canon.context = DL.theme.pageContext();
    canon.identity = DL.theme.identity();
    DL.builder.emit(canon);
  }

  function fireCollection() {
    var data = DL.adapters.shopifyLiquid.readBlock('collection');
    if (!data) return;
    var canon = DL.adapters.shopifyLiquid.fromCollection(data);
    if (!canon) return;
    canon.context = DL.theme.pageContext();
    canon.identity = DL.theme.identity();
    DL.builder.emit(canon);
  }

  function fireCart() {
    var data = DL.adapters.shopifyLiquid.readBlock('cart');
    if (!data) return;
    var canon = DL.adapters.shopifyCartAjax.fromCart(data, data.currency);
    canon.context = DL.theme.pageContext();
    canon.identity = DL.theme.identity();
    DL.builder.emit(canon);
  }

  function fireSearch() {
    var data = DL.adapters.shopifyLiquid.readBlock('search');
    if (!data) return;
    var ctx = DL.theme.pageContext();
    var idn = DL.theme.identity();

    DL.builder.emit({
      name: 'search', eventSource: 'theme',
      searchTerm: data.terms, context: ctx, identity: idn
    });

    // separate results event, with items when the snippet rendered them
    var canon = {
      name: 'view_search_results', eventSource: 'theme',
      searchTerm: data.terms, context: ctx, identity: idn
    };
    if (Array.isArray(data.products) && data.products.length) {
      var items = [];
      for (var i = 0; i < data.products.length; i++) {
        var p = data.products[i];
        items.push({
          productId: p.id, variantId: p.variant_id, sku: p.sku,
          name: p.title, brand: p.vendor, category: p.type,
          price: typeof p.price === 'number' ? { amount: p.price, unit: 'cents' } : undefined,
          quantity: 1, index: i,
          listId: 'search_results', listName: 'Search Results'
        });
      }
      canon.items = items;
      canon.currency = data.currency || DL.adapters.shopifyCartAjax.currency();
    }
    DL.builder.emit(canon);
  }

  function init() {
    firePageView();
    // each fire* is a no-op when its Liquid block isn't on this page
    fireProduct();
    fireCollection();
    fireCart();
    fireSearch();
  }

  return { init: init };
})();

/* ============================================================================
 * theme/bindings.js -- select_item + promotions (click delegation)
 *
 * Honest confidence note (from the plan): this is the LOWEST-confidence
 * surface -- product-card markup differs per theme. Selectors come from
 * config (selectors.js presets + per-store overrides) and a selector that
 * matches nothing warns in debug mode. Expect ~15 min of selector tuning on
 * a new theme; that's the platform's reality, not a template defect.
 * ========================================================================== */

DL.bindings = (function () {
  /** Find collection JSON entry for a clicked product link. */
  function productFromHref(href) {
    var data = DL.adapters.shopifyLiquid.readBlock('collection');
    if (!data || !Array.isArray(data.products)) return null;
    // match /products/<handle> (ignore query/variant params)
    var m = String(href).match(/\/products\/([^/?#]+)/);
    if (!m) return null;
    var handle = m[1];
    for (var i = 0; i < data.products.length; i++) {
      if (data.products[i].handle === handle) {
        return { product: data.products[i], index: i, list: data };
      }
    }
    return null;
  }

  function onClick(e) {
    try {
      var sel = DL.selectors.resolve();

      /* ---- select_item: click on a product link inside a product card */
      var link = e.target && e.target.closest && e.target.closest(sel.productCardLink);
      if (link && link.closest(sel.productCard)) {
        var found = productFromHref(link.getAttribute('href') || '');
        if (found) {
          var p = found.product;
          DL.builder.emit({
            name: 'select_item',
            eventSource: 'theme',
            listId: DL.normalize.id(found.list.id),
            listName: found.list.title,
            currency: found.list.currency || DL.adapters.shopifyCartAjax.currency(),
            items: [{
              productId: p.id, variantId: p.variant_id, sku: p.sku,
              name: p.title, brand: p.vendor, category: p.type,
              price: typeof p.price === 'number' ? { amount: p.price, unit: 'cents' } : undefined,
              quantity: 1, index: found.index,
              listId: DL.normalize.id(found.list.id), listName: found.list.title
            }],
            context: DL.theme.pageContext(),
            identity: DL.theme.identity()
          });
        }
        return; // a product-card click is not also a promo click
      }

      /* ---- select_promotion: click inside a promo slot */
      var promo = e.target && e.target.closest && e.target.closest(sel.promoSlot);
      if (promo) {
        var promoId = promo.getAttribute('data-promo-id') || promo.id || undefined;
        var promoName = promo.getAttribute('data-promo-name') ||
          promo.getAttribute('aria-label') || undefined;
        DL.builder.emit({
          name: 'select_promotion',
          eventSource: 'theme',
          promotionId: promoId,
          promotionName: promoName,
          context: DL.theme.pageContext(),
          identity: DL.theme.identity()
        });
      }
    } catch (err) {
      DL.debug.error('bindings click handler failed (click unaffected)', err);
    }
  }

  /** view_promotion for promo slots present at load (IntersectionObserver). */
  function observePromos() {
    if (typeof IntersectionObserver === 'undefined') return;
    var slots = DL.selectors.all('promoSlot');
    if (!slots.length) return;
    var seen = {};
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        var pid = el.getAttribute('data-promo-id') || el.id || 'promo';
        if (seen[pid]) return;
        seen[pid] = true;
        DL.builder.emit({
          name: 'view_promotion',
          eventSource: 'theme',
          promotionId: pid,
          promotionName: el.getAttribute('data-promo-name') || undefined,
          context: DL.theme.pageContext(),
          identity: DL.theme.identity()
        });
        io.unobserve(el);
      });
    }, { threshold: 0.5 });
    slots.forEach(function (el) { io.observe(el); });
  }

  function init() {
    if (typeof document === 'undefined') return;
    document.addEventListener('click', onClick, true);
    observePromos();
  }

  return { init: init };
})();

/* ============================================================================
 * theme/leads.js -- leads schema
 *
 * generate_lead (newsletter / contact form)  sign_up  login 
 * tel: / mailto: / wa.me click leads
 *
 * PII rule: form field VALUES are never pushed (the reference script pushed
 * raw form elements -- banned). Only form identity + lead type go out;
 * an email typed into a newsletter form goes out ONLY as sha256 user_data.
 * ========================================================================== */

DL.leads = (function () {
  function emitLead(leadType, formId, userData) {
    DL.builder.emit({
      name: 'generate_lead',
      eventSource: 'theme',
      leadType: leadType,
      formId: formId,
      userData: userData,             // hashed downstream; never plaintext
      context: DL.theme.pageContext(),
      identity: DL.theme.identity()
    });
  }

  function onSubmit(e) {
    try {
      var form = e.target;
      if (!form || form.nodeName !== 'FORM') return;
      // cart forms belong to the cart pipeline, not leads -- without this the
      // generic form_submit double-reports every add-to-cart (found in the
      // live Dawn dry-run, V12)
      if (form.action && String(form.action).indexOf('/cart') !== -1) return;
      var cfg = DL.config.leads;
      var sel = DL.selectors.resolve();
      var formId = form.getAttribute('id') || form.getAttribute('action') || undefined;

      // email field (if present) -> hashed user_data only
      var emailInput = form.querySelector('input[type="email"]');
      var userData = emailInput && emailInput.value ? { email: emailInput.value } : undefined;

      if (form.matches(cfg.newsletterSelector)) {
        emitLead('newsletter', formId, userData);
      } else if (form.matches(cfg.contactSelector)) {
        emitLead('contact_form', formId, userData);
      } else if (sel.signupForm && form.matches(sel.signupForm)) {
        DL.builder.emit({
          name: 'sign_up', eventSource: 'theme', formId: formId, userData: userData,
          context: DL.theme.pageContext(), identity: DL.theme.identity()
        });
      } else if (sel.loginForm && form.matches(sel.loginForm)) {
        DL.builder.emit({
          name: 'login', eventSource: 'theme', formId: formId,
          context: DL.theme.pageContext(), identity: DL.theme.identity()
        });
      } else {
        // generic form_submit -- identity only, never field values
        DL.builder.emit({
          name: 'form_submit', eventSource: 'theme', formId: formId,
          context: DL.theme.pageContext(), identity: DL.theme.identity()
        });
      }
    } catch (err) {
      DL.debug.error('leads submit handler failed (form unaffected)', err);
    }
  }

  function onClick(e) {
    try {
      var a = e.target && e.target.closest && e.target.closest('a[href]');
      if (!a) return;
      var href = a.getAttribute('href') || '';
      var cfg = DL.config.leads;
      var type = null;
      if (cfg.trackTel && href.indexOf('tel:') === 0) type = 'phone_click';
      else if (cfg.trackMailto && href.indexOf('mailto:') === 0) type = 'email_click';
      else if (cfg.trackWhatsApp && (href.indexOf('wa.me') !== -1 || href.indexOf('api.whatsapp.com') !== -1)) type = 'whatsapp_click';
      if (!type) return;

      DL.builder.emit({
        name: 'generate_lead',
        eventSource: 'theme',
        leadType: type,
        linkUrl: type === 'phone_click' || type === 'email_click' ? undefined : href, // no phone/email in the dataLayer
        context: DL.theme.pageContext(),
        identity: DL.theme.identity()
      });
    } catch (err) {
      DL.debug.error('leads click handler failed (click unaffected)', err);
    }
  }

  function init() {
    if (typeof document === 'undefined') return;
    document.addEventListener('submit', onSubmit, true);
    document.addEventListener('click', onClick, true);
  }

  return { init: init };
})();

/* ============================================================================
 * theme/index.js -- theme entry point
 *
 * Order matters:
 *   1. build config
 *   2. consent init (emits CMv2 default) -- MUST precede GTM load
 *   3. GTM load
 *   4. capture surfaces (pages, cart, bindings, leads)
 *
 * The theme owns ALL pre-checkout events and emits NOTHING inside checkout
 * (theme JS doesn't run there anyway -- Shopify's checkout is closed).
 * ========================================================================== */

DL.theme = (function () {
  /* Fresh page context per call -- never cached across pushes (C8). */
  function pageContext() {
    if (typeof document === 'undefined') return {};
    var loc = document.location || {};
    return {
      url: loc.href, referrer: document.referrer, title: document.title,
      path: loc.pathname, search: loc.search, hash: loc.hash,
      language: typeof navigator !== 'undefined' ? navigator.language : undefined,
      screenW: typeof window !== 'undefined' ? window.outerWidth : undefined,
      screenH: typeof window !== 'undefined' ? window.outerHeight : undefined,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined
    };
  }

  /* Real GA4 ids from first-party cookies (top frame -- that's us). */
  function identity() {
    return {
      clientId: DL.identity.clientIdFromCookies(),
      sessionId: DL.identity.sessionIdFromCookies(DL.config.ga4MeasurementId)
    };
  }

  function loadGtm() {
    var cfg = DL.config.gtm;
    if (!cfg.id || typeof document === 'undefined') return;
    window[cfg.dataLayerName] = window[cfg.dataLayerName] || [];
    window[cfg.dataLayerName].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
    var s = document.createElement('script');
    s.async = true;
    var dl = cfg.dataLayerName !== 'dataLayer' ? '&l=' + cfg.dataLayerName : '';
    s.src = cfg.loaderUrl + '?id=' + cfg.id + dl;   // Stape custom loader friendly
    var first = document.getElementsByTagName('script')[0];
    if (first && first.parentNode) first.parentNode.insertBefore(s, first);
    else document.head.appendChild(s);
  }

  /** Logged-in customer id from the Liquid block (never PII). */
  function emitCustomerContext() {
    var c = DL.adapters.shopifyLiquid.customer();
    if (!c || !c.id) return;
    DL.builder.emit({
      name: 'context_ready',
      eventSource: 'theme',
      customer: { id: c.id },
      context: pageContext(),
      identity: identity(),
      // email/phone from Liquid go out hashed only
      userData: (c.email || c.phone) ? { email: c.email, phone: c.phone } : undefined
    });
  }

  function start() {
    // Funnel events (page_view, view_item, add_to_cart, view_cart, search):
    // only when the theme owns them. In 'pixel' mode the custom pixel emits
    // these via Shopify standard events (theme-independent), so the theme must
    // NOT also emit -- otherwise every funnel event doubles.
    if (DL.config.eventOwnership === 'theme') {
      DL.pages.init();
      DL.cartIntercept.init();
      DL.cartDom.init();
    } else {
      DL.debug.log('theme: funnel owned by pixel -- theme runs enrichment only (select_item, promotions, leads)');
    }
    // Enrichment (DOM-only, no Shopify standard event) -- always theme-side.
    DL.bindings.init();
    DL.leads.init();
    // remove_from_cart, universally: the AJAX cart API (drawers, quick-remove,
    // quantity steppers) via cart-intercept in removals-only mode, and the
    // full-page cart via cart-page below. Adds are NOT re-emitted here -- the
    // pixel already owns those in this mode. See config.removalOwner.
    if (DL.config.eventOwnership !== 'theme') {
      DL.cartIntercept.init({ removalsOnly: true });
    }
    // Full-page cart removal (remove-link / update-cart form) -- always on:
    // Shopify fires NO product_removed_from_cart for these themes, so there is
    // nothing to double, and the pixel can't see it in 'pixel' mode either.
    DL.cartPage.init();
    emitCustomerContext();
  }

  /**
   * init(userConfig) -- call from theme.liquid:
   *   <script src="{{ 'theme.datalayer.js' | asset_url }}" defer></script>
   *   <script>window.DL_CONFIG = { gtm: { id: 'GTM-XXX' }, ... }</script>
   */
  function init(userConfig) {
    DL.buildConfig(userConfig);

    // multi-currency: shop (base) currency from the Liquid shop block ->
    // emitted as shop_currency on every event, next to presentment currency
    var shopBlock = DL.adapters.shopifyLiquid.readBlock('shop');
    if (shopBlock && shopBlock.shop_currency) {
      DL.config.shopCurrency = shopBlock.shop_currency;
    }

    // Click ids from cookies AND the current URL -- a visitor who just arrived
    // from an ad has the id in the URL before any cookie is written.
    DL.identity.readClickIds();

    // consent BEFORE GTM: CMv2 default must be on the dataLayer first
    DL.consent.init();
    loadGtm();

    if (typeof document !== 'undefined' && document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', DL.safe.wrap('theme.start', start));
    } else {
      start();
    }
    DL.debug.log('theme datalayer initialized -- all pre-checkout events');
  }

  return { init: init, pageContext: pageContext, identity: identity };
})();


/* ---- theme bootstrap ----
 * theme.liquid:
 *   <script>window.DL_CONFIG = { gtm: { id: 'GTM-XXXXXXX' }, ga4MeasurementId: 'G-XXXXXXX', debug: false };</script>
 *   <script src="{{ 'theme.datalayer.js' | asset_url }}" defer></script>
 *   {% render 'dl-data' %}
 */
window.ShopifyDataLayer = DL;
if (window.DL_CONFIG) {
  DL.theme.init(window.DL_CONFIG);
} else {
  DL.debug.warn('window.DL_CONFIG not found -- call ShopifyDataLayer.theme.init(config) manually');
}
})(typeof window !== 'undefined' ? window : this, typeof document !== 'undefined' ? document : undefined);